import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { DATE_DISPLAY_FORMAT, displayDate, toApiDate } from "../../utils/dateUtils";
import {
  formatForPhoneInput,
  formatWhatsappForPhoneInput,
  normalizeInternationalPhoneForApi,
  normalizeParentPhoneForApi,
  normalizeParentWhatsappForApi,
  normalizeStudentPhoneForApi,
  validateInternationalPhone,
  validateParentPhone,
  validateParentWhatsapp,
  validateStudentPhone,
} from "../../utils/phoneValidation";
import PersistentDialog from "../../components/common/PersistentDialog";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tab,
  Tabs,
  Menu,
  MenuItem,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddressAutocomplete from "../../components/common/AddressAutoComplete";
import Toast from "../../components/common/Toast";
import {
  Add,
  Search,
  Edit,
  Visibility,
  MoreVert,
  Download,
  Upload,
  Close,
  Delete,
  School,
  History,
} from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import api from "../../services/api";
import { createStudent, updateStudentStatus, updateStudent, deleteStudent } from "../../services/teacherService";
import ConfirmDialog from "../../components/forms/ConfirmDialog";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import PromotionHistoryLogPanel from "../../components/student/PromotionHistoryLogPanel";
import { useDepartmentModuleFilter } from "../../hooks/useDepartmentModuleFilter";
import DepartmentModuleFilterSelect from "../../components/common/DepartmentModuleFilterSelect";
import { getRolePathFromUser } from "../../utils/rolePath";
import { usePermissions } from "../../hooks/usePermissions";
import { revealStudentPii } from "../../services/piiService";
import { PII_PERMISSIONS } from "../../utils/piiPermissions";

const Students = ({
  openFromParent = false,
  openEditFromParent = false,
  parentEditData = null,
  parentPrefillLoading = false,
  onParentClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filterClassId, setFilterClassId] = useState("all");
  const [filterBatchCode, setFilterBatchCode] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [fullStudentData, setFullStudentData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteStudent, setPromoteStudent] = useState(null);
  const [promoteToClassId, setPromoteToClassId] = useState("");
  const [promoteType, setPromoteType] = useState("PROMOTION");
  const [promoteEnrollmentStatus, setPromoteEnrollmentStatus] = useState("ACTIVE");
  const [promotePracticalBatchCode, setPromotePracticalBatchCode] = useState("");
  const [promoteReason, setPromoteReason] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [promoteStep, setPromoteStep] = useState(0);
  const [promoteContext, setPromoteContext] = useState(null);
  const [promoteContextLoading, setPromoteContextLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [promoteAcademicYearId, setPromoteAcademicYearId] = useState("");
  const [promoteEffectiveDate, setPromoteEffectiveDate] = useState(null);
  const [toClassSubjects, setToClassSubjects] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [selectedBacklogSubjectIds, setSelectedBacklogSubjectIds] = useState([]);
  const [resetOptions, setResetOptions] = useState({
    resetAttendance: true,
    archivePreviousAttendance: true,
    archivePreviousExams: true,
    resetAssignmentDashboard: true,
    archivePreviousAssignments: true,
    archivePreviousMaterials: true,
  });
  const [promotionLogOpen, setPromotionLogOpen] = useState(false);
  const [promotionLogRefresh, setPromotionLogRefresh] = useState(0);

  const {
    departmentFilter,
    setDepartmentFilter,
    departmentApiParams,
    showAllDepartmentsOption,
  } = useDepartmentModuleFilter();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [dataGridKey, setDataGridKey] = useState(0);
  const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0);
  const [togglingStudentId, setTogglingStudentId] = useState(null);
  const [optimisticStatus, setOptimisticStatus] = useState({});

  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const { user: authUser } = useSelector((state) => state.auth);

  // Get current user from localStorage
  const [currentUser] = useState(() => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  });
  const rolePath = getRolePathFromUser(authUser || currentUser);

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);

  // Handle export functionality
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // Use API instance and relative paths to avoid hardcoded host
      let path = "/teacher/export-csv-teacher-students";
      let body = null;
      // Build filter params to export all matching students (not just current page)
      const exportParams = {};
      if (departmentApiParams.departmentId) exportParams.departmentId = departmentApiParams.departmentId;
      if (filterClassId && filterClassId !== "all") exportParams.classId = filterClassId;
      if (filterBatchCode && filterBatchCode !== "all") exportParams.practicalBatchCode = filterBatchCode;
      if (activeTab === 1) exportParams.status = "active";
      else if (activeTab === 2) exportParams.status = "inactive";
      if (debouncedSearchQuery) exportParams.search = debouncedSearchQuery;

      if (currentUser?.role === "hod") {
        path = "/teacher/export-csv-hod-students";
      } else if (currentUser?.role === "teacher") {
        path = "/teacher/export-csv-teacher-students";
        body = { teacherId: currentUser?.teacherId };
      } else if (["institute", "admin", "superAdmin"].includes(currentUser?.role)) {
        path = "/institute/export-csv-students";
        body = { instituteId: currentUser?.instituteId ?? selectedInstitute?.id };
      }

      const response = body
        ? await api.post(path, body, { params: exportParams, responseType: "blob" })
        : await api.post(path, undefined, { params: exportParams, responseType: "blob" });
      if (response.status !== 200) {
        throw new Error("Export failed");
      }
      const blob = response.data;
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = "my_students.csv";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export error:", error);
      let msg = error?.response?.data?.message || error?.message || "Export failed";
      showToast(msg, "error");
    } finally {
      setIsExporting(false);
    }
  };
  const [editStudentDialogOpen, setEditStudentDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    prnNumber: '',
    rollNumber: '',
    dob: null,
    phoneNumber: '',
    gender: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    parentWhatsapp: '',
    departmentId: '',
    classId: '',
    teacherId: '',
    admissionDate: null,
    startBatchDate: null,
    endBatchDate: null,
    currentSemester: '',
    currentYear: 1,
    practicalBatchCode: '',
    street: '',
    city: '',
    state: '',
    country: 'India',
    pinCode: '',
    latitude: '',
    longitude: ''
  });
  
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState({
    classes: false
  });
  const [errors, setErrors] = useState({});
  const getClassDisplayLabel = (cls) => {
    const className = cls?.name || cls?.className || "Unnamed Class";
    const division = cls?.section || cls?.classSection || cls?.division || "";
    return division ? `${className} - ${division}` : className;
  };
  const promotionClassFilterOptions = useMemo(
    () =>
      (classes || []).map((c) => {
        const className = c?.name || c?.className || "Unnamed Class";
        const division = c?.section || c?.classSection || c?.division || "";
        return {
          id: c.id || c._id,
          name: c.name,
          section: c.section,
          displayName: division ? `${className} - ${division}` : className,
        };
      }),
    [classes]
  );
  const openPromoteDialog = (studentRow) => {
    setPromoteStudent(studentRow || null);
    setPromoteToClassId("");
    setPromoteType("PROMOTION");
    setPromoteEnrollmentStatus("ACTIVE");
    setPromotePracticalBatchCode(studentRow?.batch || "");
    setPromoteReason("");
    setPromoteContext(null);
    setAcademicYears([]);
    setPromoteAcademicYearId("");
    setPromoteEffectiveDate(new Date());
    setToClassSubjects([]);
    setSelectedSubjectIds([]);
    setSelectedBacklogSubjectIds([]);
    setResetOptions({
      resetAttendance: true,
      archivePreviousAttendance: true,
      archivePreviousExams: true,
      resetAssignmentDashboard: true,
      archivePreviousAssignments: true,
      archivePreviousMaterials: true,
    });
    setPromoteStep(0);
    setPromoteDialogOpen(true);
    void fetchClasses();
  };
  const closePromoteDialog = () => {
    if (promoting) return;
    setPromoteDialogOpen(false);
    setPromoteStudent(null);
  };
  useEffect(() => {
    if (!promoteDialogOpen || !promoteStudent?.id) return;
    let isCancelled = false;
    (async () => {
      try {
        setPromoteContextLoading(true);
        const [ctxRes, yearsRes] = await Promise.all([
          api.get(`/student-promotion/context/${Number(promoteStudent.id)}`),
          api.get("/academic-info/all", { params: { page: 1, limit: 200, order: "DESC", sort: "id" } }),
        ]);
        if (isCancelled) return;
        setPromoteContext(ctxRes?.data?.data || null);
        const yearPayload = yearsRes?.data?.data;
        const yearRows =
          (Array.isArray(yearPayload?.academicYears) && yearPayload.academicYears) ||
          (Array.isArray(yearPayload?.rows) && yearPayload.rows) ||
          (Array.isArray(yearPayload?.items) && yearPayload.items) ||
          (Array.isArray(yearPayload) ? yearPayload : []);
        setAcademicYears(yearRows);
      } catch (e) {
        if (!isCancelled) {
          const msg = e?.response?.data?.message || e?.message || "Failed to load academic context";
          showToast(msg, "error");
        }
      } finally {
        if (!isCancelled) setPromoteContextLoading(false);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [promoteDialogOpen, promoteStudent?.id]);

  useEffect(() => {
    if (!promoteDialogOpen) return;
    if (!promoteToClassId) {
      setToClassSubjects([]);
      setSelectedSubjectIds([]);
      return;
    }
    let isCancelled = false;
    (async () => {
      try {
        const selectedYearRow = promoteAcademicYearId
          ? academicYears.find((y) => String(y.id) === String(promoteAcademicYearId))
          : null;
        const academicYearQuery =
          (selectedYearRow?.academicYear && String(selectedYearRow.academicYear).trim()) ||
          (promoteContext?.currentClass?.academicYear && String(promoteContext.currentClass.academicYear).trim()) ||
          undefined;
        // Correct backend route is mounted under /class-subjects
        const res = await api.get(`/class-subjects/classes/${Number(promoteToClassId)}/subjects`, {
          params: {
            ...(academicYearQuery ? { academicYear: academicYearQuery } : {}),
          },
        });
        if (isCancelled) return;
        const payload = res?.data?.data;
        const subjects =
          (Array.isArray(payload?.subjects) && payload.subjects) ||
          (Array.isArray(res?.data?.subjects) && res.data.subjects) ||
          [];
        const list = Array.isArray(subjects) ? subjects : [];
        setToClassSubjects(list);
        // Auto-select compulsory subjects by default if available
        const compulsory = list
          .filter((s) => s?.ClassSubject?.isCompulsory === true)
          .map((s) => Number(s.id))
          .filter((n) => Number.isFinite(n));
        setSelectedSubjectIds(compulsory.length ? compulsory : list.map((s) => Number(s.id)).filter((n) => Number.isFinite(n)));
      } catch (e) {
        if (!isCancelled) setToClassSubjects([]);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [
    promoteDialogOpen,
    promoteToClassId,
    promoteContext?.currentClass?.academicYear,
    promoteAcademicYearId,
    academicYears,
  ]);
  const submitPromotion = async () => {
    try {
      if (!promoteStudent?.id) {
        showToast("Please select a student", "error");
        return;
      }
      if (!promoteToClassId) {
        showToast("Please select the new class", "error");
        return;
      }
      setPromoting(true);
      await api.post("/student-promotion/promote", {
        studentId: Number(promoteStudent.id),
        toClassId: Number(promoteToClassId),
        academicYearId: promoteAcademicYearId ? Number(promoteAcademicYearId) : null,
        effectiveDate: promoteEffectiveDate ? new Date(promoteEffectiveDate).toISOString() : null,
        type: promoteType,
        enrollmentStatus: promoteEnrollmentStatus,
        practicalBatchCode: promotePracticalBatchCode || null,
        reason: promoteReason || null,
        subjectIds: selectedSubjectIds,
        backlogSubjectIds: selectedBacklogSubjectIds,
        resetOptions,
      });
      showToast("Student promoted successfully", "success");
      setPromotionLogRefresh((x) => x + 1);
      closePromoteDialog();
      await refetchStudents();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Promotion failed";
      showToast(msg, "error");
    } finally {
      setPromoting(false);
    }
  };
  const selectedClassPracticalBatches = useMemo(() => {
    const selected = classes.find((cls) => String(cls.id || cls._id) === String(newStudent.classId || ''));
    const list = Array.isArray(selected?.practicalBatches) ? selected.practicalBatches : [];
    return list
      .map((b) => String(b || '').trim().toUpperCase())
      .filter(Boolean);
  }, [classes, newStudent.classId]);

  const editingClassPracticalBatches = useMemo(() => {
    if (!editingStudent) return [];
    const selected = classes.find((cls) => String(cls.id || cls._id) === String(editingStudent.classId || ''));
    const list = Array.isArray(selected?.practicalBatches) ? selected.practicalBatches : [];
    return list
      .map((b) => String(b || '').trim().toUpperCase())
      .filter(Boolean);
  }, [classes, editingStudent]);
  
  // Toast notification state
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
  };
  
  const handleCloseToast = () => {
    setToastMessage(null);
  };

  // ---- PII reveal (unmask) ----
  const { hasPermission } = usePermissions();
  // Full reveal (hod/admin/institute/superAdmin) exposes ALL student PII,
  // including the student's own email/phone/dob.
  const canFullRevealStudentPii = hasPermission(PII_PERMISSIONS.STUDENT_REVEAL);
  // GFM mentee reveal exposes ONLY the parent-contact fields for the teacher's
  // own active mentees. It never unmasks the student's own email/phone/dob.
  const canMenteeRevealStudentPii = hasPermission(
    PII_PERMISSIONS.STUDENT_REVEAL_MENTEE
  );
  const canRevealStudentPii = canFullRevealStudentPii || canMenteeRevealStudentPii;
  // True when the caller can ONLY do the GFM (parent-contact) reveal.
  const isGfmOnlyReveal = canMenteeRevealStudentPii && !canFullRevealStudentPii;

  // A value is considered masked when the backend returns a "***"-style marker.
  const looksMasked = (v) => typeof v === "string" && v.includes("***");

  // react-phone-input-2 seeds the field with the country dial code (e.g. "91")
  // as soon as a country is selected, so an untouched OPTIONAL parent phone /
  // WhatsApp field is never a truly empty string. Treat a value that is empty or
  // carries only a country dial code (<= 4 digits, no subscriber number) as
  // "not provided" so we don't validate/require a number the user never entered.
  const isBlankPhoneValue = (v) => {
    if (v === null || v === undefined) return true;
    const digits = String(v).replace(/\D/g, "");
    return digits.length <= 4;
  };

  // Parent-contact fields a GFM mentee-reveal can unmask.
  const STUDENT_PARENT_CONTACT_FIELDS = [
    "parentName",
    "parentPhone",
    "parentEmail",
    "parentWhatsapp",
  ];

  // The set of masked fields the CURRENT user is actually allowed to reveal.
  // A full-reveal user can reveal every PII field; a GFM-only user can reveal
  // only the parent-contact subset. Used to decide whether to offer the reveal
  // action at all, so a GFM isn't prompted to reveal the student's own
  // email/phone (which they can never unmask).
  const hasRevealableMaskedPii = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    const fields = canFullRevealStudentPii
      ? STUDENT_PII_FIELDS
      : STUDENT_PARENT_CONTACT_FIELDS;
    return fields.some((f) => looksMasked(obj?.[f]));
  };

  // Value to feed react-phone-input-2. A masked PII string (e.g. "*******0508")
  // is NOT a real phone number: passing it makes the widget misparse the digits
  // into a wrong country dial code AND, because the field is controlled, fights
  // every keystroke so the user can't type. Render masked phone values as empty
  // so the field stays clean and editable until revealed or retyped.
  const phoneInputValue = (v) => (looksMasked(v) ? "" : v || "");

  // PII fields exposed by this form/detail view.
  const STUDENT_PII_FIELDS = [
    "email",
    "phoneNumber",
    "dob",
    "parentPhone",
    "parentEmail",
    "parentWhatsapp",
    "parentName",
  ];

  const [revealingStudentPii, setRevealingStudentPii] = useState(false);

  // Reveal real PII for a student and merge into LOCAL component state only.
  // Never persisted to redux/localStorage.
  const handleRevealStudentPii = async (studentId) => {
    if (!studentId || revealingStudentPii) return;
    setRevealingStudentPii(true);
    try {
      const res = await revealStudentPii(studentId);
      const real = res?.data?.data || {};

      // Only merge recognised PII fields that the backend actually returned.
      const pickPii = (target) => {
        const next = { ...(target || {}) };
        if (real.email !== undefined && real.email !== null) next.email = real.email;
        if (real.parentEmail !== undefined && real.parentEmail !== null) next.parentEmail = real.parentEmail;
        if (real.parentName !== undefined && real.parentName !== null) next.parentName = real.parentName;
        if (real.dob !== undefined && real.dob !== null) next.dob = real.dob;
        return next;
      };

      // Edit form state: reveal makes fields editable (they are already editable
      // TextFields here) and fills real values. Phone fields formatted for input.
      setEditingStudent((prev) => {
        if (!prev) return prev;
        const next = pickPii(prev);
        if (real.phoneNumber !== undefined && real.phoneNumber !== null) {
          next.phoneNumber = formatForPhoneInput(real.phoneNumber);
        }
        if (real.parentPhone !== undefined && real.parentPhone !== null) {
          next.parentPhone = formatForPhoneInput(real.parentPhone);
        }
        if (real.parentWhatsapp !== undefined && real.parentWhatsapp !== null) {
          next.parentWhatsapp = formatWhatsappForPhoneInput(real.parentWhatsapp);
        }
        return next;
      });

      // Detail view state: raw real values (no phone-input formatting).
      setFullStudentData((prev) => {
        if (!prev) return prev;
        const next = pickPii(prev);
        if (real.phoneNumber !== undefined && real.phoneNumber !== null) next.phoneNumber = real.phoneNumber;
        if (real.parentPhone !== undefined && real.parentPhone !== null) next.parentPhone = real.parentPhone;
        if (real.parentWhatsapp !== undefined && real.parentWhatsapp !== null) next.parentWhatsapp = real.parentWhatsapp;
        return next;
      });
    } catch (e) {
      // Keep masked values on failure; never crash.
      showToast(
        e?.response?.data?.message || "Could not reveal personal information",
        "error"
      );
    } finally {
      setRevealingStudentPii(false);
    }
  };

  const handleCloseAddStudentDialog = () => {
    resetForm();
    setAddStudentDialogOpen(false);
    if (typeof onParentClose === "function") {
      onParentClose();
    }
  };

  const fetchClasses = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, classes: true }));
      const params = {};
      if (departmentApiParams.departmentId) {
        params.departmentId = departmentApiParams.departmentId;
      }
      const response = await api.get('/teacher/class-names', { params });
      const body = response?.data;
      const list =
        (Array.isArray(body) && body) ||
        (Array.isArray(body?.data) && body.data) ||
        (Array.isArray(body?.data?.classes) && body.data.classes) ||
        (Array.isArray(body?.data?.rows) && body.data.rows) ||
        (Array.isArray(body?.classes) && body.classes) ||
        [];
      if (list.length) {
        setClasses(list);
      } else if (body && !Array.isArray(body)) {
        console.error("Unexpected API response format for class-names:", body);
        setClasses([]);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setClasses([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, classes: false }));
    }
  }, [departmentApiParams.departmentId]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Refresh class list (incl. practicalBatches) when add/edit dialog opens so batch dropdown is populated
  useEffect(() => {
    if (!addStudentDialogOpen && !editStudentDialogOpen) return;
    fetchClasses();
  }, [addStudentDialogOpen, editStudentDialogOpen, fetchClasses]);

  useEffect(() => {
    if (!location.state?.openCreateFromUsers) return;

    resetForm();
    setAddStudentDialogOpen(true);

    // Clear one-time navigation state so dialog doesn't reopen on refresh/back.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!openFromParent) return;
    fetchClasses();
    resetForm();
    setAddStudentDialogOpen(true);
  }, [openFromParent, fetchClasses]);

  useEffect(() => {
    if (!openEditFromParent) return;
    if (parentPrefillLoading) {
      setEditStudentDialogOpen(true);
      setEditingStudent(null);
      setErrors({});
      fetchClasses();
      return;
    }
    if (!parentEditData) return;
    fetchClasses();
    loadStudentForEdit(parentEditData);
  }, [openEditFromParent, parentEditData, parentPrefillLoading, fetchClasses]);
  
  // Function to reset the form to initial state
  const resetForm = () => {
    setNewStudent({
      firstName: '',
      lastName: '',
      email: '',
      prnNumber: '',
      rollNumber: '',
      dob: null,
      phoneNumber: '',
      gender: '',
      parentName: '',
      parentPhone: '',
      parentEmail: '',
      parentWhatsapp: '',
      departmentId: '',
      classId: '',
      teacherId: '',
      admissionDate: null,
      startBatchDate: null,
      endBatchDate: null,
      currentSemester: '',
      currentYear: 1,
      practicalBatchCode: '',
      street: '',
      city: '',
      state: '',
      country: 'India',
      pinCode: '',
      latitude: '',
      longitude: ''
    });
    setErrors({});
  };

  // Helper function to convert date to YYYY-MM-DD format for date inputs
  const formatDateForInput = (date) => {
    if (!date) return null;
    
    // If already in YYYY-MM-DD format, return as is
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }
    
    // If it's an ISO date string or Date object, convert to YYYY-MM-DD
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Error formatting date:', e);
    }
    
    return null;
  };

  // Function to load student data for editing
  const loadStudentForEdit = async (student) => {
    try {
      // Try to fetch full student details from the API response
      const raw = studentsResponse?.data?.students ||
                  studentsResponse?.data?.rows ||
                  studentsResponse?.data?.items ||
                  studentsResponse?.data ||
                  studentsResponse?.students ||
                  studentsResponse?.rows ||
                  studentsResponse?.items ||
                  [];
      
      const fullStudent = Array.isArray(raw) 
        ? raw.find(s => (s.id || s.uuid || s.studentId) === student.id)
        : null;
      
      if (fullStudent) {
        setEditingStudent({
          id: fullStudent.id || fullStudent.uuid || fullStudent.studentId,
          firstName: fullStudent.firstName || fullStudent.first_name || student.name?.split(' ')[0] || '',
          lastName: fullStudent.lastName || fullStudent.last_name || student.name?.split(' ').slice(1).join(' ') || '',
          email: fullStudent.email || student.email || '',
          prnNumber: fullStudent.prnNumber || fullStudent.prn_number || fullStudent.prnNo || fullStudent.prn_no || '',
          rollNumber: fullStudent.rollNumber || fullStudent.roll_number || fullStudent.rollNo || fullStudent.roll_no || '',
          phoneNumber: formatForPhoneInput(
            fullStudent.phoneNumber || fullStudent.phone_number || student.phone || ''
          ),
          gender: fullStudent.gender || '',
          parentName: fullStudent.parentName || fullStudent.parent_name || '',
          parentPhone: formatForPhoneInput(
            fullStudent.parentPhone || fullStudent.parent_phone || ''
          ),
          parentEmail: fullStudent.parentEmail || fullStudent.parent_email || '',
          parentWhatsapp: formatWhatsappForPhoneInput(
            fullStudent.parentWhatsapp || fullStudent.parent_whatsapp || ''
          ),
          classId: fullStudent.classId || fullStudent.class_id || student.classId || '',
          street: fullStudent.street || fullStudent.address?.street || '',
          city: fullStudent.city || fullStudent.address?.city || '',
          state: fullStudent.state || fullStudent.address?.state || '',
          country: fullStudent.country || fullStudent.address?.country || 'India',
          pinCode: fullStudent.pinCode || fullStudent.pin_code || fullStudent.address?.pinCode || '',
          dob: formatDateForInput(fullStudent.dob || fullStudent.dateOfBirth),
          admissionDate: formatDateForInput(fullStudent.admissionDate || fullStudent.admission_date),
          startBatchDate: formatDateForInput(fullStudent.startBatchDate || fullStudent.start_batch_date),
          endBatchDate: formatDateForInput(fullStudent.endBatchDate || fullStudent.end_batch_date),
          currentSemester: fullStudent.currentSemester || fullStudent.current_semester || '',
          currentYear: fullStudent.currentYear || fullStudent.current_year || 1,
          practicalBatchCode: fullStudent.practicalBatchCode || fullStudent.practical_batch_code || '',
        });
      } else {
        // Fallback to row data if full student not found
        setEditingStudent({
          id: student.id,
          firstName: student.name?.split(' ')[0] || '',
          lastName: student.name?.split(' ').slice(1).join(' ') || '',
          email: student.email || '',
          prnNumber: student.prnNumber || '',
          rollNumber: student.rollNumber || '',
          phoneNumber: formatForPhoneInput(student.phone || ''),
          gender: '',
          parentName: '',
          parentPhone: '',
          parentEmail: '',
          parentWhatsapp: '',
          classId: student.classId || '',
          street: '',
          city: '',
          state: '',
          country: 'India',
          pinCode: '',
          dob: null,
          admissionDate: null,
          startBatchDate: null,
          endBatchDate: null,
          currentSemester: '',
          currentYear: 1,
          practicalBatchCode: '',
        });
      }
      setEditStudentDialogOpen(true);
      setErrors({});
    } catch (error) {
      console.error('Error loading student for edit:', error);
      showToast('Failed to load student data. Please try again.', 'error');
    }
  };

  // Handler for edit student input changes
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    
    // currentSemester: up to 3 digits; currentYear: only 1 digit (1-8)
    if (name === 'currentSemester' && value) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 3) return;
      setEditingStudent(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === 'currentYear' && value !== '') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 1) {
        validateField('currentYear', value); // show error: only 1 digit allowed
        return;
      }
      const digit = numericValue === '' ? '' : numericValue;
      if (digit !== '' && (digit < '1' || digit > '8')) {
        validateField('currentYear', value); // show error: only 1-8 allowed
        return;
      }
      setEditingStudent(prev => ({ ...prev, [name]: digit }));
    } else {
      setEditingStudent(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    validateField(name, value);
  };

  // Block number keys in city/state/country (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleCityStateCountryChange = (e) => {
    const { name, value } = e.target;
    const stripped = value.replace(/\d/g, '');
    setNewStudent(prev => ({ ...prev, [name]: stripped }));
    validateField(name, stripped);
  };
  const handleEditCityStateCountryChange = (e) => {
    const { name, value } = e.target;
    const stripped = value.replace(/\d/g, '');
    setEditingStudent(prev => ({ ...prev, [name]: stripped }));
    validateField(name, stripped);
  };

  const sanitizeDateYear = (value) => {
    if (value == null || value === "") return "";
    const s = String(value).trim();
    if (!s) return "";
    const parts = s.split("-");
    const yearStr = parts[0] || "";
    if (!/^\d+$/.test(yearStr) || yearStr.length !== 4) return "";
    const year = parseInt(yearStr, 10);
    if (year < 1000 || year > 9999) return "";
    return s;
  };

  // Handler for edit student date changes
  const handleEditDateChange = (name, date) => {
    const nextDate = sanitizeDateYear(date);
    setEditingStudent(prev => ({
      ...prev,
      [name]: nextDate
    }));
    validateField(name, nextDate);
  };

  // Handler for edit student select changes
  const handleEditSelectChange = (name, value) => {
    setEditingStudent(prev => {
      const next = {
        ...prev,
        [name]: value
      };
      if (name === 'classId') {
        next.practicalBatchCode = '';
      }
      return next;
    });
    validateField(name, value);
  };
  
  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);
  
  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    // Helper function to safely convert value to string and trim
    const getStringValue = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val.trim();
      return String(val).trim();
    };
    
    // Helper function to check if value is empty
    const isEmpty = (val) => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string') return val.trim() === '';
      if (typeof val === 'number') return false; // Numbers are never empty
      return String(val).trim() === '';
    };
    
    // List of optional fields that should not be validated as required
    const optionalFields = [
      'lastName', 'street', 'city', 'state', 'country', 'pinCode', 
      'dob', 'currentSemester', 'currentYear', 'latitude', 'longitude',
      'departmentId', 'teacherId'
    ];
    
    switch (name) {
      case 'firstName':
        const firstNameStr = getStringValue(value);
        if (!firstNameStr) {
          newErrors.firstName = 'First name is required';
        } else if (firstNameStr.length < 2) {
          newErrors.firstName = 'First name must be at least 2 characters';
        } else if (!/^[A-Za-z\s]+$/.test(firstNameStr)) {
          newErrors.firstName = 'First name can only contain letters and spaces';
        } else if (firstNameStr.length > 50) {
          newErrors.firstName = 'First name cannot exceed 50 characters';
        } else {
          delete newErrors.firstName;
        }
        break;
      case 'email':
        const emailStr = getStringValue(value);
        if (!emailStr) {
          newErrors.email = 'Email is required';
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailStr)) {
          newErrors.email = 'Please enter a valid email';
        } else {
          delete newErrors.email;
        }
        break;
      case 'phoneNumber': {
        const phoneError = editStudentDialogOpen
          ? validateStudentPhone(value)
          : validateInternationalPhone(value);
        if (phoneError) {
          newErrors.phoneNumber = phoneError;
        } else {
          delete newErrors.phoneNumber;
        }
        break;
      }
      case 'parentPhone': {
        // Parent phone is optional; only validate when a real number is provided
        // (a bare country dial code from the phone widget counts as blank).
        if (isBlankPhoneValue(value)) {
          delete newErrors.parentPhone;
          break;
        }
        const parentPhoneError = editStudentDialogOpen
          ? validateParentPhone(value)
          : validateInternationalPhone(value);
        if (parentPhoneError) {
          newErrors.parentPhone = parentPhoneError;
        } else {
          delete newErrors.parentPhone;
        }
        break;
      }
      case 'parentEmail':
        const parentEmailStr = getStringValue(value);
        // Parent email is optional; only validate when a value is provided
        if (!parentEmailStr) {
          delete newErrors.parentEmail;
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(parentEmailStr)) {
          newErrors.parentEmail = 'Please enter a valid parent email';
        } else {
          delete newErrors.parentEmail;
        }
        break;
      case 'parentWhatsapp': {
        // Parent WhatsApp is optional; only validate when a real number is
        // provided (a bare country dial code from the widget counts as blank).
        if (isBlankPhoneValue(value)) {
          delete newErrors.parentWhatsapp;
          break;
        }
        const whatsappError = validateParentWhatsapp(value);
        if (whatsappError) {
          newErrors.parentWhatsapp = whatsappError;
        } else {
          delete newErrors.parentWhatsapp;
        }
        break;
      }
      case 'gender':
        if (isEmpty(value)) {
          newErrors.gender = 'Gender is required';
        } else {
          delete newErrors.gender;
        }
        break;
      case 'classId':
        // classId can be a number or string, just check if it exists
        if (value === '' || value === null || value === undefined) {
          newErrors.classId = 'Class is required';
        } else {
          delete newErrors.classId;
        }
        break;
      case 'parentName':
        const parentNameStr = getStringValue(value);
        // Parent name is optional; only validate format when a value is provided
        if (!parentNameStr) {
          delete newErrors.parentName;
        } else if (!/^[A-Za-z\s]+$/.test(parentNameStr)) {
          newErrors.parentName = 'Parent name can only contain letters and spaces';
        } else if (parentNameStr.length < 2) {
          newErrors.parentName = 'Parent name must be at least 2 characters';
        } else if (parentNameStr.length > 50) {
          newErrors.parentName = 'Parent name cannot exceed 50 characters';
        } else {
          delete newErrors.parentName;
        }
        break;
      case 'pinCode':
        const pinCodeStr = getStringValue(value);
        if (pinCodeStr && !/^[0-9]{6}$/.test(pinCodeStr)) {
          newErrors.pinCode = 'Please enter a valid 6-digit pincode';
        } else {
          delete newErrors.pinCode;
        }
        break;
      case 'currentSemester':
        const semesterStr = getStringValue(value);
        if (semesterStr) {
          if (!/^\d{1,3}$/.test(semesterStr)) {
            newErrors.currentSemester = 'Semester must be 1-3 digits';
          } else {
            const semesterNum = parseInt(semesterStr, 10);
            if (semesterNum < 1 || semesterNum > 12) {
              newErrors.currentSemester = 'Semester must be between 1 and 12';
            } else {
              delete newErrors.currentSemester;
            }
          }
        } else {
          delete newErrors.currentSemester;
        }
        break;
      case 'currentYear':
        const yearStr = getStringValue(value);
        if (yearStr) {
          if (!/^[1-8]$/.test(yearStr)) {
            newErrors.currentYear = 'Enter 1 digit (1-8)';
          } else {
            delete newErrors.currentYear;
          }
        } else {
          delete newErrors.currentYear;
        }
        break;
      case 'dob':
        if (value && value.trim() !== '') {
          const dobTrimmed = value.trim();
          const dobYearPart = dobTrimmed.split('-')[0] || '';
          if (dobYearPart.length > 4) {
            newErrors.dob = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
          } else if (/^\d{1,4}$/.test(dobTrimmed) || (dobYearPart.length > 0 && dobTrimmed.indexOf('-') === -1)) {
            newErrors.dob = "Please enter full date (YYYY-MM-DD), not only year.";
          } else {
          const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dobRegex.test(dobTrimmed)) {
            newErrors.dob = "Please enter a valid date (YYYY-MM-DD)";
          } else {
            const dob = new Date(value);
            const currentYear = new Date().getFullYear();
            const birthYear = dob.getFullYear();
            
            if (isNaN(dob.getTime())) {
              newErrors.dob = "Please enter a valid date";
            } else if (birthYear > currentYear - 5) {
              newErrors.dob = "Student must be at least 5 years old";
            } else if (birthYear < currentYear - 100) {
              newErrors.dob = "Please enter a valid birth year";
            } else if (dob > new Date()) {
              newErrors.dob = "Date of Birth cannot be in the future";
            } else {
              delete newErrors.dob;
            }
          }
          }
        } else {
          delete newErrors.dob;
        }
        break;
      case 'admissionDate':
        if (value && value.trim() !== '') {
          const admissionTrimmed = value.trim();
          const admissionYearPart = admissionTrimmed.split('-')[0] || '';
          if (admissionYearPart.length > 4) {
            newErrors.admissionDate = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
          } else if (/^\d{1,4}$/.test(admissionTrimmed) || (admissionYearPart.length > 0 && admissionTrimmed.indexOf('-') === -1)) {
            newErrors.admissionDate = "Please enter full date (YYYY-MM-DD), not only year.";
          } else {
          const admissionRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!admissionRegex.test(admissionTrimmed)) {
            newErrors.admissionDate = "Please enter a valid date (YYYY-MM-DD)";
          } else {
            const admissionDate = new Date(value);
            const currentYear = new Date().getFullYear();
            const admissionYear = admissionDate.getFullYear();
            
            if (isNaN(admissionDate.getTime())) {
              newErrors.admissionDate = "Please enter a valid date";
            } else if (admissionYear < currentYear - 20) {
              newErrors.admissionDate = "Admission year cannot be more than 20 years in the past";
            } else if (admissionYear > currentYear + 2) {
              newErrors.admissionDate = "Admission year cannot be more than 2 years in the future";
            } else {
              delete newErrors.admissionDate;
            }
          }
          }
        } else {
          delete newErrors.admissionDate;
        }
        break;
      case 'startBatchDate':
        if (value && value.trim() !== '') {
          const batchStartTrimmed = value.trim();
          const batchStartYearPart = batchStartTrimmed.split('-')[0] || '';
          if (batchStartYearPart.length > 4) {
            newErrors.startBatchDate = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
          } else if (/^\d{1,4}$/.test(batchStartTrimmed) || (batchStartYearPart.length > 0 && batchStartTrimmed.indexOf('-') === -1)) {
            newErrors.startBatchDate = "Please enter full date (YYYY-MM-DD), not only year.";
          } else {
          const batchStartRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!batchStartRegex.test(batchStartTrimmed)) {
            newErrors.startBatchDate = "Please enter a valid date (YYYY-MM-DD)";
          } else {
            const batchStartDate = new Date(value);
            const currentYear = new Date().getFullYear();
            const batchStartYear = batchStartDate.getFullYear();
            
            if (isNaN(batchStartDate.getTime())) {
              newErrors.startBatchDate = "Please enter a valid date";
            } else if (batchStartYear < currentYear - 10) {
              newErrors.startBatchDate = "Batch start year cannot be more than 10 years in the past";
            } else if (batchStartYear > currentYear + 5) {
              newErrors.startBatchDate = "Batch start year cannot be more than 5 years in the future";
            } else {
              delete newErrors.startBatchDate;
            }
          }
          }
        } else {
          delete newErrors.startBatchDate;
        }
        break;
      case 'endBatchDate':
        if (value && value.trim() !== '') {
          const batchEndTrimmed = value.trim();
          const batchEndYearPart = batchEndTrimmed.split('-')[0] || '';
          if (batchEndYearPart.length > 4) {
            newErrors.endBatchDate = "Year cannot exceed 4 digits (YYYY). Please enter full date as YYYY-MM-DD.";
          } else if (/^\d{1,4}$/.test(batchEndTrimmed) || (batchEndYearPart.length > 0 && batchEndTrimmed.indexOf('-') === -1)) {
            newErrors.endBatchDate = "Please enter full date (YYYY-MM-DD), not only year.";
          } else {
          const batchEndRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!batchEndRegex.test(batchEndTrimmed)) {
            newErrors.endBatchDate = "Please enter a valid date (YYYY-MM-DD)";
          } else {
            const batchEndDate = new Date(value);
            const currentYear = new Date().getFullYear();
            const batchEndYear = batchEndDate.getFullYear();
            
            if (isNaN(batchEndDate.getTime())) {
              newErrors.endBatchDate = "Please enter a valid date";
            } else if (batchEndYear < currentYear - 10) {
              newErrors.endBatchDate = "Batch end year cannot be more than 10 years in the past";
            } else if (batchEndYear > currentYear + 10) {
              newErrors.endBatchDate = "Batch end year cannot be more than 10 years in the future";
            } else {
              // Check if end date is after start date (if start date is provided)
              if (newStudent.startBatchDate && !newErrors.startBatchDate) {
                const startDate = new Date(newStudent.startBatchDate);
                if (!isNaN(startDate.getTime()) && batchEndDate <= startDate) {
                  newErrors.endBatchDate = "Batch end date must be after batch start date";
                } else {
                  delete newErrors.endBatchDate;
                }
              } else {
                delete newErrors.endBatchDate;
              }
            }
          }
          }
        } else {
          delete newErrors.endBatchDate;
        }
        break;
      default:
        // Only validate required fields, skip optional ones
        if (!optionalFields.includes(name)) {
          if (isEmpty(value)) {
            newErrors[name] = 'This field is required';
          } else {
            delete newErrors[name];
          }
        } else {
          // For optional fields, only validate format if value is provided
          delete newErrors[name];
        }
    }
    
    setErrors(newErrors);
    // Return true if this specific field has no error
    return !newErrors[name];
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle semester and year inputs
    if (name === 'currentSemester' && value) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 2) return;
      setNewStudent(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === 'currentYear' && value !== '') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 1) {
        validateField('currentYear', value); // show error: only 1 digit allowed
        return;
      }
      const digit = numericValue === '' ? '' : numericValue;
      if (digit !== '' && (digit < '1' || digit > '8')) {
        validateField('currentYear', value); // show error: only 1-8 allowed
        return;
      }
      setNewStudent(prev => ({ ...prev, [name]: digit }));
    } else {
      setNewStudent(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    validateField(name, value);
  };
  
  const handleDateChange = (name, date) => {
    const nextDate = sanitizeDateYear(date);
    setNewStudent(prev => ({
      ...prev,
      [name]: nextDate
    }));
    validateField(name, nextDate);
  };
  
  const handleSelectChange = (name, value) => {
    setNewStudent(prev => {
      const next = {
        ...prev,
        [name]: value
      };
      if (name === 'classId') {
        next.practicalBatchCode = '';
      }
      return next;
    });
    validateField(name, value);
  };

  const handleClassChange = (value) => {
    handleSelectChange('classId', value);
  };
  
  const handlePhoneChange = (value, name) => {
    setNewStudent(prev => ({
      ...prev,
      [name]: value
    }));
    validateField(name, value);
  };
  
  const handleAddressSelect = (address) => {
    const updates = {
      street: address.street || '',
      city: address.city || '',
      state: address.state || '',
      country: address.country || 'India',
      pinCode: address.pinCode || '',
      latitude: address.latitude?.toString() || '',
      longitude: address.longitude?.toString() || ''
    };
    
    setNewStudent(prev => ({
      ...prev,
      ...updates
    }));
    
    // Validate all address fields
    Object.entries(updates).forEach(([key, value]) => {
      if (key === 'latitude' || key === 'longitude') return;
      validateField(key, value);
    });
  };
  
  // const handleDepartmentChange = (value) => {
  //   handleSelectChange('departmentId', value);
  //   // Here you would typically fetch classes based on the selected department
  //   // fetchClassesByDepartment(value).then(data => setClasses(data));
  // };
  
  // const handleClassChange = (value) => {
  //   handleSelectChange('classId', value);
  //   // Here you would typically fetch teachers based on the selected class
  //   // fetchTeachersByClass(value).then(data => setTeachers(data));
  // };
  
  // const handlePhoneChange = (value, name) => {
  //   setNewStudent(prev => ({
  //     ...prev,
  //     [name]: value
  //   }));
  //   validateField(name, value);
  // };
  
  const disableFutureDates = (current) => {
    return current && current > new Date().endOf('day');
  };
  
  const validateForm = () => {
    // Only validate required fields
    // Parent details (parentName, parentPhone, parentEmail, parentWhatsapp) are optional.
    const requiredFields = ['firstName', 'email', 'dob', 'phoneNumber', 'gender', 'classId', 'admissionDate', 'startBatchDate', 'endBatchDate'];
    let isValid = true;
    const newErrors = {};
    
    // Helper function to safely get string value
    const getStringValue = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val.trim();
      return String(val).trim();
    };
    
    // Helper function to check if value is empty
    const isEmpty = (val) => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string') return val.trim() === '';
      if (typeof val === 'number') return false; // Numbers are never empty
      return String(val).trim() === '';
    };
    
    requiredFields.forEach(field => {
      const value = newStudent[field];
      if (isEmpty(value)) {
        newErrors[field] = `${field === 'classId' ? 'Class' : field === 'dob' ? 'Date of birth' : field === 'admissionDate' ? 'Admission date' : field === 'startBatchDate' ? 'Batch start date' : field === 'endBatchDate' ? 'Batch end date' : field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        isValid = false;
      } else {
        // Validate format for specific fields
        const valueStr = getStringValue(value);
        if (field === 'email' && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(valueStr)) {
          newErrors[field] = 'Please enter a valid email';
          isValid = false;
        } else if (field === 'phoneNumber') {
          const phoneError = validateInternationalPhone(value);
          if (phoneError) {
            newErrors[field] = phoneError;
            isValid = false;
          }
        } else if (field === 'parentPhone') {
          const parentPhoneError = validateInternationalPhone(value);
          if (parentPhoneError) {
            newErrors[field] = parentPhoneError;
            isValid = false;
          }
        } else if (field === 'parentWhatsapp') {
          const whatsappError = validateParentWhatsapp(value);
          if (whatsappError) {
            newErrors[field] = whatsappError;
            isValid = false;
          }
        } else if (field === 'firstName') {
          if (valueStr.length < 2) {
            newErrors[field] = 'First name must be at least 2 characters';
            isValid = false;
          } else if (valueStr.length > 50) {
            newErrors[field] = 'First name cannot exceed 50 characters';
            isValid = false;
          } else if (!/^[A-Za-z\s]+$/.test(valueStr)) {
            newErrors[field] = 'First name can only contain letters and spaces';
            isValid = false;
          }
        } else if (field === 'parentName') {
          if (!/^[A-Za-z\s]+$/.test(valueStr)) {
            newErrors[field] = 'Parent name can only contain letters and spaces';
            isValid = false;
          } else if (valueStr.length < 2) {
            newErrors[field] = 'Parent name must be at least 2 characters';
            isValid = false;
          } else if (valueStr.length > 50) {
            newErrors[field] = 'Parent name cannot exceed 50 characters';
            isValid = false;
          }
        } else if (field === 'dob') {
          const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dobRegex.test(valueStr)) {
            newErrors[field] = 'Please enter a valid date (YYYY-MM-DD)';
            isValid = false;
          } else {
            const dob = new Date(valueStr);
            if (isNaN(dob.getTime())) {
              newErrors[field] = 'Please enter a valid date';
              isValid = false;
            } else if (dob > new Date()) {
              newErrors[field] = 'Date of birth cannot be in the future';
              isValid = false;
            } else {
              const age = (new Date() - dob) / (365.25 * 24 * 60 * 60 * 1000);
              if (age < 5) {
                newErrors[field] = 'Student must be at least 5 years old';
                isValid = false;
              }
            }
          }
        }
      }
    });
    
    // Validate optional fields if they have values
    const pinCodeStr = getStringValue(newStudent.pinCode);
    if (pinCodeStr && !/^[0-9]{6}$/.test(pinCodeStr)) {
      newErrors.pinCode = 'Please enter a valid 6-digit pincode';
      isValid = false;
    }

    // Parent details are optional — validate format only when a value is provided
    const parentNameStr = getStringValue(newStudent.parentName);
    if (parentNameStr) {
      if (!/^[A-Za-z\s]+$/.test(parentNameStr)) {
        newErrors.parentName = 'Parent name can only contain letters and spaces';
        isValid = false;
      } else if (parentNameStr.length < 2) {
        newErrors.parentName = 'Parent name must be at least 2 characters';
        isValid = false;
      } else if (parentNameStr.length > 50) {
        newErrors.parentName = 'Parent name cannot exceed 50 characters';
        isValid = false;
      }
    }

    if (!isBlankPhoneValue(newStudent.parentPhone)) {
      const parentPhoneError = editStudentDialogOpen
        ? validateParentPhone(newStudent.parentPhone)
        : validateInternationalPhone(newStudent.parentPhone);
      if (parentPhoneError) {
        newErrors.parentPhone = parentPhoneError;
        isValid = false;
      }
    }

    const parentEmailStr = getStringValue(newStudent.parentEmail);
    if (parentEmailStr && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(parentEmailStr)) {
      newErrors.parentEmail = 'Please enter a valid parent email';
      isValid = false;
    }

    if (!isBlankPhoneValue(newStudent.parentWhatsapp)) {
      const whatsappError = validateParentWhatsapp(newStudent.parentWhatsapp);
      if (whatsappError) {
        newErrors.parentWhatsapp = whatsappError;
        isValid = false;
      }
    }
    
    const currentSemesterStr = getStringValue(newStudent.currentSemester);
    if (currentSemesterStr) {
      if (!/^\d{1,3}$/.test(currentSemesterStr)) {
        newErrors.currentSemester = 'Semester must be 1-3 digits';
        isValid = false;
      } else {
        const semesterNum = parseInt(currentSemesterStr, 10);
        if (semesterNum < 1 || semesterNum > 12) {
          newErrors.currentSemester = 'Semester must be between 1 and 12';
          isValid = false;
        }
      }
    }
    
    const currentYearStr = getStringValue(newStudent.currentYear);
    if (currentYearStr) {
      if (!/^[1-8]$/.test(currentYearStr)) {
        newErrors.currentYear = 'Enter 1 digit (1-8)';
        isValid = false;
      }
    }
    
    // Validate batch dates relationship
    const startDateStr = getStringValue(newStudent.startBatchDate);
    const endDateStr = getStringValue(newStudent.endBatchDate);
    
    if (startDateStr && endDateStr) {
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate <= startDate) {
        newErrors.endBatchDate = "Batch end date must be after batch start date";
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    return isValid;
  };







  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddStudent = async () => {
    try {
      // Validate form before proceeding
      if (!validateForm()) {
        console.error('Form validation failed');
        showToast('Please fill in all required fields correctly.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      setIsSubmitting(true);
      
      // Format dates to YYYY-MM-DD format if they exist
      const formatDate = (date) => {
        if (!date) return null;
        if (typeof date === 'string') {
          // If it's already a string, check if it's in the right format
          if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
          // Try to parse and format
          const d = new Date(date);
          if (isNaN(d.getTime())) return null;
          return d.toISOString().split('T')[0];
        }
        if (date instanceof Date) {
          return date.toISOString().split('T')[0];
        }
        return null;
      };
      
      // Helper to clean string values (trim and convert empty to null)
      const cleanString = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
      };
      
      // Helper to convert to number or return null
      const toNumber = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const num = Number(value);
        return isNaN(num) ? null : num;
      };
      
      // The normalizers return "" for an empty (not-provided) value and null for a
      // malformed one. Student phone is REQUIRED, so an empty/malformed value ("" or
      // null → both falsy) blocks submit. Parent phone/WhatsApp are OPTIONAL: an empty
      // value ("") is allowed and passes through; only a non-empty-but-malformed value
      // (strictly === null) blocks submit.
      const normalizedPhone = normalizeInternationalPhoneForApi(newStudent.phoneNumber);
      // A bare country dial code (e.g. "91") is treated as "not provided" for the
      // optional parent fields, so it normalizes to "" (allowed) rather than null.
      const normalizedParentPhone = isBlankPhoneValue(newStudent.parentPhone)
        ? ""
        : normalizeInternationalPhoneForApi(newStudent.parentPhone);
      const normalizedParentWhatsapp = isBlankPhoneValue(newStudent.parentWhatsapp)
        ? ""
        : normalizeParentWhatsappForApi(newStudent.parentWhatsapp);

      const parentPhoneInvalid = normalizedParentPhone === null;
      const parentWhatsappInvalid = normalizedParentWhatsapp === null;

      if (!normalizedPhone || parentPhoneInvalid || parentWhatsappInvalid) {
        const phoneErrors = {};
        if (!normalizedPhone) phoneErrors.phoneNumber = validateInternationalPhone(newStudent.phoneNumber);
        if (parentPhoneInvalid) phoneErrors.parentPhone = validateInternationalPhone(newStudent.parentPhone);
        if (parentWhatsappInvalid) phoneErrors.parentWhatsapp = validateParentWhatsapp(newStudent.parentWhatsapp);
        setErrors((prev) => ({ ...prev, ...phoneErrors }));
        showToast('Please fix phone number fields before submitting.', 'error');
        setIsSubmitting(false);
        return;
      }

      // Prepare the student data in the required format
      // Ensure all required fields are present and properly formatted
      const studentData = {
        firstName: cleanString(newStudent.firstName),
        email: cleanString(newStudent.email),
        prnNumber: cleanString(newStudent.prnNumber),
        rollNumber: cleanString(newStudent.rollNumber),
        phoneNumber: normalizedPhone,
        gender: cleanString(newStudent.gender),
        classId: toNumber(newStudent.classId), // Convert to number
        practicalBatchCode: cleanString(newStudent.practicalBatchCode),
        parentName: cleanString(newStudent.parentName),
        parentPhone: normalizedParentPhone,
        parentEmail: cleanString(newStudent.parentEmail)?.toLowerCase(),
        parentWhatsapp: normalizedParentWhatsapp,
      };

      // Add optional fields only if they have values
      const lastName = cleanString(newStudent.lastName);
      if (lastName) {
        studentData.lastName = lastName;
      }
      
      // Build fullName
      const firstName = cleanString(newStudent.firstName);
      studentData.fullName = lastName 
        ? `${firstName} ${lastName}`.trim()
        : firstName;
      
      const dob = formatDate(newStudent.dob);
      studentData.dob = dob || null;
      
      const admissionDate = formatDate(newStudent.admissionDate);
      if (admissionDate) {
        studentData.admissionDate = admissionDate;
      }
      
      // Address fields (only if provided)
      const street = cleanString(newStudent.street);
      if (street) studentData.street = street;
      
      const city = cleanString(newStudent.city);
      if (city) studentData.city = city;
      
      const state = cleanString(newStudent.state);
      if (state) studentData.state = state;
      
      const country = cleanString(newStudent.country) || 'India';
      studentData.country = country;
      
      const pinCode = cleanString(newStudent.pinCode);
      if (pinCode) {
        studentData.pinCode = pinCode;
      }

      // Batch dates (now required - always include if present)
      const startBatchDate = formatDate(newStudent.startBatchDate);
      if (startBatchDate) {
        studentData.startBatchDate = startBatchDate;
      }
      
      const endBatchDate = formatDate(newStudent.endBatchDate);
      if (endBatchDate) {
        studentData.endBatchDate = endBatchDate;
      }

      // Current semester and year
      const currentSemester = cleanString(newStudent.currentSemester);
      if (currentSemester) {
        studentData.currentSemester = currentSemester;
      }
      
      if (newStudent.currentYear) {
        studentData.currentYear = Number(newStudent.currentYear);
      }

      // Final validation before sending
      if (!studentData.classId || isNaN(studentData.classId)) {
        showToast('Invalid class selected. Please select a valid class.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      if (!studentData.firstName || !studentData.email || !studentData.dob || !studentData.phoneNumber || 
          !studentData.gender || !studentData.parentName || !studentData.parentPhone ||
          !studentData.parentEmail || !studentData.parentWhatsapp ||
          !studentData.admissionDate || !studentData.startBatchDate || !studentData.endBatchDate) {
        showToast('Please fill in all required fields.', 'error');
        setIsSubmitting(false);
        return;
      }
      
      // Ensure classId is a number
      studentData.classId = Number(studentData.classId);

      // Call the API
      await createStudent(studentData);

      // Close the dialog and reset the form
      handleCloseAddStudentDialog();
      
      // Show success message
      showToast('Student added successfully!', 'success');
      
      // Invalidate and refetch the students list
      queryClient.invalidateQueries({
        queryKey: ['institute-students'],
        exact: false
      });
      
      // Force refetch to get the updated list
      await refetchStudents();
      
      // Reset to first page to see the new student
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      
    } catch (error) {
      console.error('Error adding student:', {
        message: error?.message,
        status: error?.response?.status,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      
      // Show detailed error message
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error ||
                         error.message || 
                         'Failed to add student. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };



  // ========= API: Institute Students Listing =========
  // Uses: GET /institute/get-all-institutes-students?search=&page=1&limit=10&sort=createdAt&order=DESC (newest first)
  // SuperAdmin in institute context uses selectedInstitute; institute/hod/teacher use currentUser.instituteId
  const effectiveInstituteId = currentUser?.instituteId ?? selectedInstitute?.id;
  useEffect(() => {
    const t=setTimeout(()=>setDebouncedSearchQuery(searchQuery),400);
    return ()=>clearTimeout(t);
  },[searchQuery]);

  const { 
    data: studentsResponse, 
    isLoading: studentsLoading, 
    isFetching: studentsFetching,
    error: studentsError,
    refetch: refetchStudents
  } = useQuery({
    queryKey: [
      "institute-students", 
      { 
        page: paginationModel.page + 1, 
        pageSize: paginationModel.pageSize,
        search: debouncedSearchQuery,
        status: activeTab ===1 ? "active" : activeTab===2 ? "inactive" :"all",
        classId: filterClassId,
        practicalBatchCode: filterBatchCode,
        sort: 'createdAt',
        order: 'DESC',
        instituteId: effectiveInstituteId,
        departmentId: departmentApiParams.departmentId,
      }
    ],
    queryFn: async () => {
      const params = { 
        page: paginationModel.page + 1, 
        limit: paginationModel.pageSize,
        search: debouncedSearchQuery || undefined,
        status: activeTab===1?"active":activeTab===2?"inactive":undefined,
        classId: (filterClassId && filterClassId !== "all") ? filterClassId : undefined,
        practicalBatchCode: (filterBatchCode && filterBatchCode !== "all") ? filterBatchCode : undefined,
        sort: 'createdAt',
        order: 'DESC'
      };
      if (effectiveInstituteId) params.instituteId = effectiveInstituteId;
      if (departmentApiParams.departmentId) params.departmentId = departmentApiParams.departmentId;
      const res = await api.get("/institute/get-all-institutes-students", { params });
      return res.data;
    },
    staleTime: 0, // Always refetch when query key changes
  });

  // Mutation for updating student status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      return await updateStudentStatus(id, status);
    },
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries(['institute-students']);
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData([
        "institute-students", 
        { 
          page: paginationModel.page + 1, 
          pageSize: paginationModel.pageSize,
          search: searchQuery,
          sort: 'firstName',
          order: 'ASC'
        }
      ]);
      
      // Optimistically update the cache
      queryClient.setQueryData(
        [
          "institute-students", 
          { 
            page: paginationModel.page + 1, 
            pageSize: paginationModel.pageSize,
            search: searchQuery,
            sort: 'firstName',
            order: 'ASC'
          }
        ],
        (oldData) => {
          if (!oldData) return oldData;
          
          const updateStatus = (data) => {
            if (Array.isArray(data)) {
              return data.map(s => {
                const studentId = s.id || s.uuid || s.studentId;
                // Use Number comparison to handle string/number mismatches
                if (Number(studentId) === Number(id)) {
                  return { ...s, status: status };
                }
                return s;
              });
            }
            if (data?.data?.students) {
              return {
                ...data,
                data: {
                  ...data.data,
                  students: updateStatus(data.data.students)
                }
              };
            }
            if (data?.data?.rows) {
              return {
                ...data,
                data: {
                  ...data.data,
                  rows: updateStatus(data.data.rows)
                }
              };
            }
            if (data?.data?.items) {
              return {
                ...data,
                data: {
                  ...data.data,
                  items: updateStatus(data.data.items)
                }
              };
            }
            if (data?.data && Array.isArray(data.data)) {
              return {
                ...data,
                data: updateStatus(data.data)
              };
            }
            return data;
          };
          
          return updateStatus(oldData);
        }
      );
      
      // Force useMemo recalculation immediately
      setStatusUpdateTrigger(prev => prev + 1);
      
      return { previousData };
    },
    onSuccess: async (responseData, variables) => {
      console.log('Status update success:', { responseData, variables });
      
      // Extract status from API response: { data: { status: true, studentId: 31 } }
      const apiStatus = responseData?.data?.status;
      const studentId = responseData?.data?.studentId || variables.id;
      
      // Update cache with API response data
      queryClient.setQueryData(
        [
          "institute-students", 
          { 
            page: paginationModel.page + 1, 
            pageSize: paginationModel.pageSize,
            search: searchQuery,
            sort: 'firstName',
            order: 'ASC'
          }
        ],
        (oldData) => {
          if (!oldData) return oldData;
          
          const updateStatus = (data) => {
            if (Array.isArray(data)) {
              return data.map(s => {
                const sid = s.id || s.uuid || s.studentId;
                if (Number(sid) === Number(studentId) || Number(sid) === Number(variables.id)) {
                  return { ...s, status: apiStatus !== undefined ? apiStatus : variables.status };
                }
                return s;
              });
            }
            if (data?.data?.students) {
              return {
                ...data,
                data: {
                  ...data.data,
                  students: updateStatus(data.data.students)
                }
              };
            }
            if (data?.data?.rows) {
              return {
                ...data,
                data: {
                  ...data.data,
                  rows: updateStatus(data.data.rows)
                }
              };
            }
            if (data?.data?.items) {
              return {
                ...data,
                data: {
                  ...data.data,
                  items: updateStatus(data.data.items)
                }
              };
            }
            if (data?.data && Array.isArray(data.data)) {
              return {
                ...data,
                data: updateStatus(data.data)
              };
            }
            return data;
          };
          
          return updateStatus(oldData);
        }
      );
      
      // Invalidate and refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['institute-students'],
        exact: false
      });
      
      // Force refetch
      await refetchStudents();
      
      // Force useMemo recalculation and DataGrid re-render
      setStatusUpdateTrigger(prev => prev + 1);
      
      // Small delay to ensure cache update is processed
      setTimeout(() => {
        setDataGridKey(prev => prev + 1);
      }, 50);
      
      showToast('Student status updated successfully!', 'success');
    },
    onError: (error, variables, context) => {
      // Rollback to previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(
          [
            "institute-students", 
            { 
              page: paginationModel.page + 1, 
              pageSize: paginationModel.pageSize,
              search: searchQuery,
              sort: 'firstName',
              order: 'ASC'
            }
          ],
          context.previousData
        );
      }
      console.error('Error updating student status:', error);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to update status. Please try again.';
      showToast(errorMessage, 'error');
      // Refetch to ensure UI is in sync
      refetchStudents();
    },
  });

  // Handler for status toggle
  const handleStatusToggle = async (row, currentStatus) => {
    const id = row?.id;
    if (!id) {
      showToast('Student ID not found. Please refresh the page.', 'error');
      return;
    }
    const newStatus = currentStatus === 'active' ? false : true;
    setTogglingStudentId(id);
    setOptimisticStatus((prev) => ({ ...prev, [id]: newStatus }));
    updateStatusMutation.mutate(
      { id, status: newStatus },
      {
        onError: () => {
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
        onSettled: () => {
          setTogglingStudentId(null);
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
      }
    );
  };

  // Reset pagination when class or batch filter changes (server-side pagination)
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [filterClassId, filterBatchCode, activeTab, debouncedSearchQuery]);

  // Reset Class and Batch filters when Department changes
  useEffect(() => {
    setFilterClassId("all");
    setFilterBatchCode("all");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [departmentFilter]);

  // Mutation for updating student
  const updateStudentMutation = useMutation({
    mutationFn: async ({ id, studentData }) => {
      return await updateStudent(id, studentData);
    },
    onSuccess: () => {
      // Invalidate queries to trigger automatic refetch
      queryClient.invalidateQueries(['institute-students']);
      // Close dialog and reset state immediately
      setEditStudentDialogOpen(false);
      setEditingStudent(null);
      setErrors({});
      showToast('Student updated successfully!', 'success');
    },
    onError: (error) => {
      console.error('Error updating student:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message || 
                          'Failed to update student. Please try again.';
      showToast(errorMessage, 'error');
      // Don't close dialog on error so user can retry
    },
  });

  // Mutation for deleting student
  const deleteStudentMutation = useMutation({
    mutationFn: async (studentDbId) => {
      return await deleteStudent(studentDbId);
    },
    onSuccess: () => {
      // Invalidate queries to trigger automatic refetch
      queryClient.invalidateQueries(['institute-students']);
      // Close dialog and reset state immediately
      setDeleteConfirmOpen(false);
      setStudentToDelete(null);
      showToast('Student deleted successfully!', 'success');
    },
    onError: (error) => {
      console.error('Error deleting student:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message || 
                          'Failed to delete student. Please try again.';
      showToast(errorMessage, 'error');
      // Close dialog on error too to prevent stuck state
      setDeleteConfirmOpen(false);
      setStudentToDelete(null);
    },
  });

  // Validate edit form
  const validateEditForm = () => {
    if (!editingStudent) return { isValid: false, newErrors: {} };
    
    // Parent details (parentName, parentPhone, parentEmail, parentWhatsapp) are optional.
    const requiredFields = ['firstName', 'phoneNumber', 'gender', 'classId', 'admissionDate', 'startBatchDate', 'endBatchDate'];
    let isValid = true;
    const newErrors = {};
    
    const getStringValue = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val.trim();
      return String(val).trim();
    };
    
    const isEmpty = (val) => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string') return val.trim() === '';
      if (typeof val === 'number') return false;
      return String(val).trim() === '';
    };
    
    requiredFields.forEach(field => {
      const value = editingStudent[field];
      if (isEmpty(value)) {
        newErrors[field] = `${field === 'classId' ? 'Class' : field === 'admissionDate' ? 'Admission date' : field === 'startBatchDate' ? 'Batch start date' : field === 'endBatchDate' ? 'Batch end date' : field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        isValid = false;
      } else {
        const valueStr = getStringValue(value);
        if (field === 'phoneNumber') {
          // A still-masked phone means the user has not revealed/changed it;
          // it will be omitted from the payload so the backend keeps the real
          // value. Do not validate the mask marker as if it were a number.
          if (!looksMasked(value)) {
            const phoneError = validateStudentPhone(value);
            if (phoneError) {
              newErrors[field] = phoneError;
              isValid = false;
            }
          }
        } else if (field === 'parentPhone') {
          if (!looksMasked(value)) {
            const parentPhoneError = validateParentPhone(value);
            if (parentPhoneError) {
              newErrors[field] = parentPhoneError;
              isValid = false;
            }
          }
        } else if (field === 'parentWhatsapp') {
          if (!looksMasked(value)) {
            const whatsappError = validateParentWhatsapp(value);
            if (whatsappError) {
              newErrors[field] = whatsappError;
              isValid = false;
            }
          }
        } else if (field === 'parentEmail' && !looksMasked(valueStr) && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(valueStr)) {
          newErrors[field] = 'Please enter a valid parent email';
          isValid = false;
        } else if (field === 'parentName' && looksMasked(valueStr)) {
          // Untouched masked parent name; omitted from payload, keep as-is.
        } else if (field === 'firstName') {
          if (valueStr.length < 2) {
            newErrors[field] = 'First name must be at least 2 characters';
            isValid = false;
          } else if (valueStr.length > 50) {
            newErrors[field] = 'First name cannot exceed 50 characters';
            isValid = false;
          } else if (!/^[A-Za-z\s]+$/.test(valueStr)) {
            newErrors[field] = 'First name can only contain letters and spaces';
            isValid = false;
          }
        } else if (field === 'parentName') {
          if (!/^[A-Za-z\s]+$/.test(valueStr)) {
            newErrors[field] = 'Parent name can only contain letters and spaces';
            isValid = false;
          } else if (valueStr.length < 2) {
            newErrors[field] = 'Parent name must be at least 2 characters';
            isValid = false;
          } else if (valueStr.length > 50) {
            newErrors[field] = 'Parent name cannot exceed 50 characters';
            isValid = false;
          }
        }
      }
    });

    // Parent details are optional — validate format only when a value is provided
    // and not still masked (masked values are omitted from the payload).
    const editParentName = editingStudent.parentName;
    if (!isEmpty(editParentName) && !looksMasked(editParentName)) {
      const editParentNameStr = getStringValue(editParentName);
      if (!/^[A-Za-z\s]+$/.test(editParentNameStr)) {
        newErrors.parentName = 'Parent name can only contain letters and spaces';
        isValid = false;
      } else if (editParentNameStr.length < 2) {
        newErrors.parentName = 'Parent name must be at least 2 characters';
        isValid = false;
      } else if (editParentNameStr.length > 50) {
        newErrors.parentName = 'Parent name cannot exceed 50 characters';
        isValid = false;
      }
    }

    if (!isEmpty(editingStudent.parentPhone) && !looksMasked(editingStudent.parentPhone)) {
      const parentPhoneError = validateParentPhone(editingStudent.parentPhone);
      if (parentPhoneError) {
        newErrors.parentPhone = parentPhoneError;
        isValid = false;
      }
    }

    const editParentEmailStr = getStringValue(editingStudent.parentEmail);
    if (
      editParentEmailStr &&
      !looksMasked(editParentEmailStr) &&
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(editParentEmailStr)
    ) {
      newErrors.parentEmail = 'Please enter a valid parent email';
      isValid = false;
    }

    if (!isEmpty(editingStudent.parentWhatsapp) && !looksMasked(editingStudent.parentWhatsapp)) {
      const whatsappError = validateParentWhatsapp(editingStudent.parentWhatsapp);
      if (whatsappError) {
        newErrors.parentWhatsapp = whatsappError;
        isValid = false;
      }
    }

    const pinCodeStr = getStringValue(editingStudent.pinCode);
    if (pinCodeStr && !/^[0-9]{6}$/.test(pinCodeStr)) {
      newErrors.pinCode = 'Please enter a valid 6-digit pincode';
      isValid = false;
    }
    
    // Validate batch dates relationship
    const startDateStr = getStringValue(editingStudent.startBatchDate);
    const endDateStr = getStringValue(editingStudent.endBatchDate);
    
    if (startDateStr && endDateStr) {
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && endDate <= startDate) {
        newErrors.endBatchDate = "Batch end date must be after batch start date";
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    return { isValid, newErrors };
  };

  useEffect(() => {
    if (editStudentDialogOpen && editingStudent) {
      validateEditForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingStudent, editStudentDialogOpen]);

  // Handler for edit student
  const handleEditStudent = async () => {
    if (!editingStudent) return;
    
    try {
      const { isValid, newErrors } = validateEditForm();
      if (!isValid) {
        const msgs = Object.values(newErrors).filter(Boolean);
        const firstMsg = msgs.length ? msgs[0] : 'Please fill in all required fields correctly.';
        const toastMsg = msgs.length > 1 ? msgs.join('; ') : firstMsg;
        showToast(toastMsg, 'error');

        const firstField = Object.keys(newErrors).find(k => newErrors[k]);
        if (firstField) {
          const byId = document.getElementById(`${firstField}_edit`) || document.getElementsByName(firstField)[0];
          if (byId && typeof byId.focus === 'function') {
            setTimeout(() => byId.focus(), 50);
          }
        }
        return;
      }

      // Format dates
      const formatDate = (date) => {
        if (!date) return null;
        if (typeof date === 'string') {
          if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
          const d = new Date(date);
          if (isNaN(d.getTime())) return null;
          return d.toISOString().split('T')[0];
        }
        if (date instanceof Date) {
          return date.toISOString().split('T')[0];
        }
        return null;
      };

      const cleanString = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        return trimmed === '' ? null : trimmed;
      };

      // Ensure we have a valid ID
      const studentId = Number(editingStudent.id);
      if (!studentId || isNaN(studentId)) {
        showToast('Invalid student ID. Please refresh the page and try again.', 'error');
        return;
      }

      // PII fields that are still masked mean the user never revealed or edited
      // them. They must be OMITTED from the payload so the backend keeps the
      // existing real value — never validated (the "***" marker is not a phone
      // number) and never sent (which would overwrite the real value with a mask).
      const phoneMasked = looksMasked(editingStudent.phoneNumber);
      const parentPhoneMasked = looksMasked(editingStudent.parentPhone);
      const parentWhatsappMasked = looksMasked(editingStudent.parentWhatsapp);

      // Parent phone / WhatsApp are OPTIONAL. A blank value (empty, or a bare
      // country dial code like "+91" the widget seeds) means "not provided" and
      // must NOT be treated as an error. Only a non-empty-but-malformed value
      // blocks submit. Student's own phone remains REQUIRED.
      const parentPhoneBlank = isBlankPhoneValue(editingStudent.parentPhone);
      const parentWhatsappBlank = isBlankPhoneValue(editingStudent.parentWhatsapp);

      const normalizedPhone = phoneMasked
        ? null
        : normalizeStudentPhoneForApi(editingStudent.phoneNumber);
      const normalizedParentPhone = parentPhoneMasked
        ? null
        : parentPhoneBlank
          ? ""
          : normalizeParentPhoneForApi(editingStudent.parentPhone);
      const normalizedParentWhatsapp = parentWhatsappMasked
        ? null
        : parentWhatsappBlank
          ? ""
          : normalizeParentWhatsappForApi(editingStudent.parentWhatsapp);

      // Only block submit when a field was actually edited (not masked) yet
      // normalizes to an invalid value. For the optional parent fields, a blank
      // value normalizes to "" and is allowed; only a strictly `null` (non-empty
      // but malformed) value is an error.
      const phoneErrors = {};
      if (!phoneMasked && !normalizedPhone) {
        phoneErrors.phoneNumber = validateStudentPhone(editingStudent.phoneNumber);
      }
      if (!parentPhoneMasked && !parentPhoneBlank && normalizedParentPhone === null) {
        phoneErrors.parentPhone = validateParentPhone(editingStudent.parentPhone, { required: false });
      }
      if (!parentWhatsappMasked && !parentWhatsappBlank && normalizedParentWhatsapp === null) {
        phoneErrors.parentWhatsapp = validateParentWhatsapp(editingStudent.parentWhatsapp, { required: false });
      }
      if (Object.values(phoneErrors).some(Boolean)) {
        setErrors((prev) => ({ ...prev, ...phoneErrors }));
        showToast('Please fix phone number fields before submitting.', 'error');
        return;
      }

      const parentNameMasked = looksMasked(editingStudent.parentName);
      const parentEmailMasked = looksMasked(editingStudent.parentEmail);

      const studentData = {
        firstName: cleanString(editingStudent.firstName),
        lastName: cleanString(editingStudent.lastName) || '',
        prnNumber: cleanString(editingStudent.prnNumber),
        rollNumber: cleanString(editingStudent.rollNumber),
        // Masked (untouched) → null so the cleanup step below drops it.
        phoneNumber: phoneMasked ? null : normalizedPhone,
        dob: formatDate(editingStudent.dob),
        gender: cleanString(editingStudent.gender),
        classId: Number(editingStudent.classId),
        practicalBatchCode: cleanString(editingStudent.practicalBatchCode),
        parentName: parentNameMasked ? null : cleanString(editingStudent.parentName),
        parentPhone: parentPhoneMasked ? null : normalizedParentPhone,
        parentEmail: parentEmailMasked
          ? null
          : cleanString(editingStudent.parentEmail)?.toLowerCase(),
        parentWhatsapp: parentWhatsappMasked ? null : normalizedParentWhatsapp,
        street: cleanString(editingStudent.street) || '',
        city: cleanString(editingStudent.city) || '',
        state: cleanString(editingStudent.state) || '',
        country: cleanString(editingStudent.country) || 'India',
        pinCode: cleanString(editingStudent.pinCode) || '',
        admissionDate: formatDate(editingStudent.admissionDate),
        startBatchDate: formatDate(editingStudent.startBatchDate),
        endBatchDate: formatDate(editingStudent.endBatchDate),
        currentSemester: cleanString(editingStudent.currentSemester),
        currentYear: editingStudent.currentYear ? Number(editingStudent.currentYear) : null,
      };

      // Remove empty optional fields (but never remove 'country')
      Object.keys(studentData).forEach((key) => {
        const v = studentData[key];
        if (v === null || v === "" || (typeof v === "number" && !Number.isFinite(v))) {
          if (key !== "country") {
            delete studentData[key];
          }
        }
      });

      console.log('Updating student with ID:', studentId, 'and data:', studentData); // Debug log
      updateStudentMutation.mutate({ id: studentId, studentData });
    } catch (error) {
      console.error('Error in handleEditStudent:', error);
      showToast('Failed to update student. Please try again.', 'error');
    }
  };

  // Handler for delete student
  const handleDeleteStudent = () => {
    if (studentToDelete) {
      deleteStudentMutation.mutate(studentToDelete.id);
    }
  };

  // Normalize API response into rows expected by DataGrid
  const students = useMemo(() => {
    // Include statusUpdateTrigger to force recalculation when status changes
    const _ = statusUpdateTrigger;
    // Try common shapes: { data: { students: [] } }, { data: { rows: [] } }, { data: [] }
    const raw =
      studentsResponse?.data?.students ||
      studentsResponse?.data?.rows ||
      studentsResponse?.data?.items ||
      studentsResponse?.data ||
      studentsResponse?.students ||
      studentsResponse?.rows ||
      studentsResponse?.items ||
      [];

    return (raw || []).map((s, index) => {
      const firstName = s.firstName || s.first_name || "";
      const lastName = s.lastName || s.last_name || "";
      const fullName =
        s.fullName ||
        s.name ||
        `${firstName} ${lastName}`.trim() ||
        `Student ${index + 1}`;

      return {
        id: s.id || s.uuid || s.studentId || s.studentLoginID || index + 1,
        studentPk: s.id || null, // DB primary key for API calls where required
        studentId: s.studentId || s.studentLoginID || s.id || s.uuid || null, // system Student ID (immutable)
        studentLoginID: s.studentLoginID || s.studentId || null, // legacy/mobile compatibility
        classId: s.classId || s.class_id || null,
        name: fullName,
        // Roll number is the institute-assigned rollNumber field (separate from system Student ID).
        rollNo: s.rollNumber || s.roll_number || s.rollNo || "-",
        prnNumber: s.prnNumber || s.prn_number || s.prnNo || s.prn_no || "-",
        rollNumber: s.rollNumber || s.roll_number || s.roll_no || s.rollNo || "-",
        className: s.className || s.class_name || s.class || s.classId || "N/A",
        classSection: s.classSection || s.class_section || s.section || "N/A",
        batch: s.practicalBatchCode || s.practical_batch_code || s.batch || "-",
        department: s.departmentName || s.department_name || s.department || "N/A",
        email: s.email || "",
        phone: s.phoneNumber || s.phone_number || s.phone || "",
        profileImage: s.profileImage || null,
        // Normalize status: handle boolean true/false and string "active"/"inactive"
        // API returns boolean true/false, convert to "active"/"inactive" for UI
        status: (s.status === false || s.status === "inactive" || s.status === 0) 
          ? "inactive" 
          : (s.status === true || s.status === "active" || s.status === 1) 
            ? "active" 
            : "active", // Default to active if unclear
        // Attendance intentionally not surfaced here: the backend does not provide a
        // reliable per-student value for this list, and fabricating one is misleading.
        // Use the dedicated attendance/report screens for real figures.
      };
    });
  }, [studentsResponse, statusUpdateTrigger]);

  const classBatchOptions = useMemo(() => {
    const selected = classes.find((cls) => String(cls.id || cls._id) === String(filterClassId || ''));
    const list = Array.isArray(selected?.practicalBatches) ? selected.practicalBatches : [];
    return list.map((b) => String(b || '').trim().toUpperCase()).filter(Boolean);
  }, [classes, filterClassId]);

  const columns = [
    {
      field: "name",
      headerName: "Student",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar src={params.row.profileImage || undefined} sx={{ width: 32, height: 32 }}>
            {!params.row.profileImage ? params.row.name[0] : null}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Student ID: {params.row.rollNo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "prnNumber", headerName: "PRN", width: 150 },
    { field: "rollNumber", headerName: "Roll Number", width: 160 },
    { field: "className", headerName: "Class", width: 100 },
    { field: "classSection", headerName: "Section", width: 100 },
    { field: "batch", headerName: "Batch", width: 100 },
    { field: "department", headerName: "Department", width: 260 },
    { field: "email", headerName: "Email", width: 260 },
    { field: "phone", headerName: "Phone", width: 130 },
    // Attendance column commented out
    // {
    //   field: "attendance",
    //   headerName: "Attendance",
    //   width: 120,
    //   renderCell: (params) => (
    //     <Chip
    //       label={`${params.value}%`}
    //       size="small"
    //       color={
    //         params.value >= 85
    //           ? "success"
    //           : params.value >= 75
    //           ? "warning"
    //           : "error"
    //       }
    //     />
    //   ),
    // },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const rowId = params.row?.id;
        if (rowId == null) return null;
        const isActive = optimisticStatus[rowId] ?? (params.value === "active" || params.value === true);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} key={rowId}>
            <Switch
              key={rowId}
              checked={Boolean(isActive)}
              onChange={(e) => {
                e.stopPropagation();
                handleStatusToggle(params.row, params.value);
              }}
              disabled={togglingStudentId === rowId}
              size="small"
              color="primary"
            />
            <Typography
              variant="body2"
              color={isActive ? "success.main" : "text.secondary"}
              sx={{ fontWeight: isActive ? 500 : 400 }}
            >
              {isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedStudent(params.row);
                // Find full student data from API response
                const raw = studentsResponse?.data?.students ||
                            studentsResponse?.data?.rows ||
                            studentsResponse?.data?.items ||
                            studentsResponse?.data ||
                            studentsResponse?.students ||
                            studentsResponse?.rows ||
                            studentsResponse?.items ||
                            [];
                const fullData = Array.isArray(raw) 
                  ? raw.find(s => {
                      const sid = s.id || s.uuid || s.studentId;
                      return Number(sid) === Number(params.row.id);
                    })
                  : null;
                setFullStudentData(fullData || params.row);
                setAnchorEl(null);
                setViewDialogOpen(true);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <PermissionGate permission="student.profile.update">
            <Tooltip title="Edit">
              <IconButton 
                size="small"
                onClick={() => {
                  loadStudentForEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="student.profile.delete">
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => {
                  setStudentToDelete(params.row);
                  setDeleteConfirmOpen(true);
                }}
                sx={{ color: 'error.main' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
           <PermissionGate permission="student.profile.update">
            <Tooltip title="Promote">
              <IconButton
                size="small"
                onClick={() => openPromoteDialog(params.row)}
                sx={{ color: "primary.main" }}
              >
                <School fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate> 
          {/* <Tooltip title="More options">
            <IconButton
              size="small"
              onClick={(e) => {
                setSelectedStudent(params.row);
                setAnchorEl(e.currentTarget);
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Box>
      ),
    },
  ];

  const filteredStudents = students;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            My Students
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage students in your class
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGate permissions={["student.promotion.logs", "student.profile.read"]}>
            <Button variant="outlined" startIcon={<History />} onClick={() => navigate(`${rolePath}/promotion-history`)}>
              Promotion log
            </Button>
          </PermissionGate>
          <PermissionGate permission="user.export">
            <Tooltip title="Exported files have masked personal information. Contact support if you need the full personal information.">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleExport}
                  disabled={isExporting || filteredStudents.length === 0}
                >
                  {isExporting ? "Exporting" : "Export"}
                </Button>
              </span>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="student.profile.create">
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
          </PermissionGate>
          <PermissionGate permission="student.profile.create">
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setAddStudentDialogOpen(true);
              }}
            >
              Add Student
            </Button>
          </PermissionGate>
        </Box>
      </Box>
      
      {/* Add Student Dialog */}
      <PersistentDialog 
        open={addStudentDialogOpen} 
        onClose={handleCloseAddStudentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Student</DialogTitle>
        
<DialogContent>
  <Box sx={{ mt: 2 }}>
    <Grid container spacing={2}>
      {/* First Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name="firstName"
          label="First Name *"
          value={newStudent.firstName}
          onChange={handleInputChange}
          onKeyPress={(e) => {
            if (!/[A-Za-z\s]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const paste = e.clipboardData.getData('text');
            if (!/^[A-Za-z\s]*$/.test(paste)) {
              e.preventDefault();
            }
          }}
          error={!!errors.firstName}
          helperText={errors.firstName || (newStudent.firstName.length < 2 ? "Only letters, min 2 characters" : "")}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>

      {/* Last Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name="lastName"
          label="Last Name"
          value={newStudent.lastName}
          onChange={handleInputChange}
          onKeyPress={(e) => {
            if (!/[A-Za-z\s]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            const paste = e.clipboardData.getData('text');
            if (!/^[A-Za-z\s]*$/.test(paste)) {
              e.preventDefault();
            }
          }}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>

      {/* Email */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name="email"
          label="Email *"
          type="email"
          value={newStudent.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={errors.email}
          placeholder="e.g. student@example.com"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField fullWidth name="prnNumber" label="PRN Number" value={newStudent.prnNumber} onChange={handleInputChange} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField fullWidth name="rollNumber" label="Roll Number" value={newStudent.rollNumber} onChange={handleInputChange} />
      </Grid>

      {/* DOB */}
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Date of Birth *"
          format={DATE_DISPLAY_FORMAT}
          value={newStudent.dob ? new Date(newStudent.dob) : null}
          onChange={(val) => {
            const next = toApiDate(val);
            handleDateChange("dob", next);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !!errors.dob,
              helperText: errors.dob,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl
          fullWidth
          variant="outlined"
          error={!!errors.phoneNumber}
        >
          <InputLabel   sx={{ top: '-5px' }}  shrink htmlFor="phoneNumber">
            Phone *
          </InputLabel>

          <PhoneInput
            country="in"
            specialLabel=""
            inputProps={{
              id: 'phoneNumber',
              name: 'phoneNumber',
              'aria-label': 'Phone'
            }}
            countryCodeEditable={false}
            enableSearch
            value={newStudent.phoneNumber}
            onChange={(value) => {
              setNewStudent(prev => ({
                ...prev,
                phoneNumber: value
              }));
              validateField('phoneNumber', value);
            }}
            containerStyle={{
              marginTop: '4px'
            }}
            inputStyle={{
              width: '100%',
              height: '56px',
              paddingLeft: '48px',
              borderRadius: '4px',
              border: errors.phoneNumber
                ? '1px solid #d32f2f'
                : '1px solid rgba(0, 0, 0, 0.23)'
            }}
          />

          {errors.phoneNumber && (
            <FormHelperText>
              {errors.phoneNumber}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>


      {/* Gender */}
      <Grid item xs={12} sm={6}>
        <TextField 
          select 
          fullWidth 
          name="gender"
          label="Gender *" 
          value={newStudent.gender}
          onChange={handleInputChange}
          error={!!errors.gender}
          helperText={errors.gender}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
      </Grid>

      {/* Parent Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name="parentName"
          label="Parent Name"
          value={newStudent.parentName}
          onChange={handleInputChange}
          onKeyPress={(e) => {
            if (!/[A-Za-z\s]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          error={!!errors.parentName}
          helperText={errors.parentName}
          placeholder="e.g. Amit Sharma"
        />
      </Grid>

{/* Parent Phone */}
<Grid item xs={12} sm={6}>
  <FormControl
    fullWidth
    variant="outlined"
    error={!!errors.parentPhone}
  >
    <InputLabel   sx={{ top: '-5px' }}  shrink htmlFor="parentPhone">
      Parent Phone
    </InputLabel>

    <PhoneInput
      country="in"
      specialLabel=""
      inputProps={{
        id: 'parentPhone',
        name: 'parentPhone',
        'aria-label': 'Parent Phone'
      }}
      countryCodeEditable={false}
      enableSearch
      value={newStudent.parentPhone}
      onChange={(value) => {
        setNewStudent(prev => ({
          ...prev,
          parentPhone: value
        }));
        validateField('parentPhone', value);
      }}
      containerStyle={{
        marginTop: '4px'
      }}
      inputStyle={{
        width: '100%',
        height: '56px',
        paddingLeft: '48px',
        borderRadius: '4px',
        border: errors.parentPhone
          ? '1px solid #d32f2f'
          : '1px solid rgba(0, 0, 0, 0.23)'
      }}
    />

    {errors.parentPhone && (
      <FormHelperText>
        {errors.parentPhone}
      </FormHelperText>
    )}
  </FormControl>
</Grid>

      {/* Parent Email */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          name="parentEmail"
          type="email"
          label="Parent Email"
          value={newStudent.parentEmail}
          onChange={handleInputChange}
          onBlur={(e) => validateField('parentEmail', e.target.value)}
          error={!!errors.parentEmail}
          helperText={errors.parentEmail}
          placeholder="parent@example.com"
        />
      </Grid>

      {/* Parent WhatsApp */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth variant="outlined" error={!!errors.parentWhatsapp}>
          <InputLabel sx={{ top: '-5px' }} shrink htmlFor="parentWhatsapp">
            Parent WhatsApp
          </InputLabel>
          <PhoneInput
            country="in"
            specialLabel=""
            inputProps={{ id: 'parentWhatsapp', name: 'parentWhatsapp', 'aria-label': 'Parent WhatsApp' }}
            countryCodeEditable={false}
            enableSearch
            value={newStudent.parentWhatsapp}
            onChange={(value) => {
              const formatted = value ? (value.startsWith('+') ? value : `+${value}`) : '';
              setNewStudent((prev) => ({ ...prev, parentWhatsapp: formatted }));
              validateField('parentWhatsapp', formatted);
            }}
            containerStyle={{
              marginTop: '4px',
            }}
            inputStyle={{
              width: '100%',
              height: '56px',
              paddingLeft: '48px',
              borderRadius: '4px',
              border: errors.parentWhatsapp ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
            }}
          />
          {errors.parentWhatsapp && <FormHelperText>{errors.parentWhatsapp}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Class */}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          name="classId"
          label="Class *"
          value={newStudent.classId || ''}
          onChange={(e) => handleSelectChange('classId', e.target.value)}
          error={!!errors.classId}
          helperText={errors.classId || (isLoading.classes ? 'Loading classes...' : '')}
          disabled={isLoading.classes}
          SelectProps={{
            startAdornment: isLoading.classes && (
              <InputAdornment position="start">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">Select Class</MenuItem>
          {Array.isArray(classes) && classes.map((cls) => (
            <MenuItem key={cls.id || cls._id} value={cls.id || cls._id}>
              {getClassDisplayLabel(cls)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Practical Batch */}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          name="practicalBatchCode"
          label="Practical Batch"
          value={newStudent.practicalBatchCode || ''}
          onChange={(e) => handleSelectChange('practicalBatchCode', e.target.value)}
          helperText={
            selectedClassPracticalBatches.length > 0
              ? 'Optional. Choose one of the configured class batches.'
              : 'No practical batches configured for this class'
          }
          disabled={!newStudent.classId || selectedClassPracticalBatches.length === 0}
        >
          <MenuItem value="">None</MenuItem>
          {selectedClassPracticalBatches.map((batchCode) => (
            <MenuItem key={batchCode} value={batchCode}>
              {batchCode}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Admission Date */}
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Admission Date *"
          format={DATE_DISPLAY_FORMAT}
          value={newStudent.admissionDate ? new Date(newStudent.admissionDate) : null}
          onChange={(val) => {
            const next = toApiDate(val);
            handleDateChange("admissionDate", next);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !!errors.admissionDate,
              helperText: errors.admissionDate,
            },
          }}
        />
      </Grid>

      {/* Batch Start Date */}
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Batch Start Date *"
          format={DATE_DISPLAY_FORMAT}
          value={newStudent.startBatchDate ? new Date(newStudent.startBatchDate) : null}
          onChange={(val) => {
            const next = toApiDate(val);
            handleDateChange("startBatchDate", next);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !!errors.startBatchDate,
              helperText: errors.startBatchDate,
            },
          }}
        />
      </Grid>

      {/* Batch End Date */}
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Batch End Date *"
          format={DATE_DISPLAY_FORMAT}
          value={newStudent.endBatchDate ? new Date(newStudent.endBatchDate) : null}
          onChange={(val) => {
            const next = toApiDate(val);
            handleDateChange("endBatchDate", next);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              error: !!errors.endBatchDate,
              helperText: errors.endBatchDate,
            },
          }}
        />
      </Grid>

      {/* Current Semester */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          name="currentSemester"
          label="Current Semester"
          value={newStudent.currentSemester}
          onChange={handleInputChange}
          inputProps={{ 
            min: 1, 
            max: 12,
            maxLength: 2,
            inputMode: 'numeric',
            pattern: '\d{1,2}'
          }}
          error={!!errors.currentSemester}
          helperText={errors.currentSemester || "Enter 1-2 digits"}
          placeholder="e.g. 3"
        />
      </Grid>

      {/* Current Year */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          name="currentYear"
          label="Current Year"
          value={newStudent.currentYear}
          onChange={handleInputChange}
                    inputProps={{ 
            min: 1, 
            max: 8,
            maxLength: 1,
            inputMode: 'numeric',
            pattern: '[1-8]'
          }}
          error={!!errors.currentYear}
          helperText={errors.currentYear || "Enter 1 digit (1-8)"}
          placeholder="e.g. 2"
        />
      </Grid>

      {/* Address Search */}
      <Grid item xs={12}>
        <AddressAutocomplete
          placeholder="Search address (e.g. Chandigarh, Delhi)"
          countryRestriction="in"
          onAddressSelect={handleAddressSelect}
        />
      </Grid>

      {/* Street */}
      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth 
          name="street" 
          label="Street" 
          value={newStudent.street}
          onChange={handleInputChange}
          error={!!errors.street}
          helperText={errors.street}
          placeholder="e.g. MG Road, Near City Mall"
        />
      </Grid>

      {/* City */}
      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth 
          name="city" 
          label="City" 
          value={newStudent.city}
          onChange={handleCityStateCountryChange}
          onKeyDown={blockNumberKeyDown}
          error={!!errors.city}
          helperText={errors.city}
          placeholder="e.g. Kanpur"
        />
      </Grid>

      {/* State */}
      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth 
          name="state" 
          label="State" 
          value={newStudent.state}
          onChange={handleCityStateCountryChange}
          onKeyDown={blockNumberKeyDown}
          error={!!errors.state}
          helperText={errors.state}
          placeholder="e.g. Uttar Pradesh"
        />
      </Grid>

      {/* Country */}
      <Grid item xs={12} sm={6}>
        <TextField 
          fullWidth 
          name="country" 
          label="Country" 
          value={newStudent.country}
          onChange={handleCityStateCountryChange}
          onKeyDown={blockNumberKeyDown}
          error={!!errors.country}
          helperText={errors.country}
          placeholder="e.g. India"
        />
      </Grid>

      {/* Pincode */}
      <Grid item xs={12} sm={6}>
       <TextField
  fullWidth
  name="pinCode"
  label="Pincode"
  value={newStudent.pinCode || ""}
  onChange={(e) => {
    // allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, "");

    setNewStudent({
      ...newStudent,
      pinCode: value,
    });

    // clear error while typing
    if (errors.pinCode) {
      setErrors(prev => {
        const { pinCode, ...rest } = prev || {};
        return rest;
      });
    }
  }}
  inputProps={{ maxLength: 9 }}
  error={
    !!errors.pinCode ||
    (newStudent.pinCode &&
      (newStudent.pinCode.length < 3 ||
        newStudent.pinCode.length > 9))
  }
  helperText={
    errors.pinCode ||
    (newStudent.pinCode &&
      (newStudent.pinCode.length < 3 ||
        newStudent.pinCode.length > 9)
      ? "Pincode must be between 3 and 9 digits"
      : "")
  }
  placeholder="e.g. 226010"
/>


        
      </Grid>
    </Grid>
  </Box>
</DialogContent>


        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseAddStudentDialog}
            color="inherit"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddStudent}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <Add />}
          >
            {isSubmitting ? 'Adding...' : 'Add Student'}
          </Button>
        </DialogActions>
      </PersistentDialog>

      {/* Edit Student Dialog */}
      <Dialog 
        open={editStudentDialogOpen} 
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setEditStudentDialogOpen(false);
          setEditingStudent(null);
          setErrors({});
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Edit Student</span>
          {canRevealStudentPii && hasRevealableMaskedPii(editingStudent) && (
            <Tooltip
              title={
                isGfmOnlyReveal
                  ? "Reveal parent contact details (Guardian Faculty access)"
                  : "Reveal personal information"
              }
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="reveal personal information"
                  disabled={revealingStudentPii}
                  onClick={() => handleRevealStudentPii(editingStudent?.id)}
                >
                  {revealingStudentPii ? <CircularProgress size={18} /> : <Visibility fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent>
          {parentPrefillLoading && !editingStudent ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : editingStudent ? (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="firstName"
                    label="First Name *"
                    value={editingStudent.firstName || ''}
                    onChange={handleEditInputChange}
                    onKeyPress={(e) => {
                      if (!/[A-Za-z\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    error={!!errors.firstName}
                    helperText={errors.firstName || ((editingStudent.firstName || "").length < 2 ? "Only letters, min 2 characters" : "")}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="lastName"
                    label="Last Name"
                    value={editingStudent.lastName || ''}
                    onChange={handleEditInputChange}
                    onKeyPress={(e) => {
                      if (!/[A-Za-z\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="prnNumber" label="PRN Number" value={editingStudent.prnNumber || ''} onChange={handleEditInputChange} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField fullWidth name="rollNumber" label="Roll Number" value={editingStudent.rollNumber || ''} onChange={handleEditInputChange} />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.phoneNumber}>
                    <InputLabel shrink htmlFor="phoneNumber_edit">Phone *</InputLabel>
                    <PhoneInput
                      country="in"
                      placeholder=""
                      specialLabel=""
                      inputProps={{ id: 'phoneNumber_edit', name: 'phoneNumber', 'aria-label': 'Phone' }}
                      countryCodeEditable={false}
                      enableSearch
                      value={phoneInputValue(editingStudent.phoneNumber)}
                      onChange={(value) => {
                        setEditingStudent(prev => ({
                          ...prev,
                          phoneNumber: value
                        }));
                        validateField('phoneNumber', value);
                      }}
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        borderRadius: '4px',
                        border: errors.phoneNumber ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)'
                      }}
                      containerStyle={{
                        marginTop: '8px',
                        marginBottom: errors.phoneNumber ? '8px' : '23px'
                      }}
                    />
                    {errors.phoneNumber && (
                      <FormHelperText>{errors.phoneNumber}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* DOB */}
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Date of Birth"
                    format={DATE_DISPLAY_FORMAT}
                    value={editingStudent.dob ? new Date(editingStudent.dob) : null}
                    onChange={(val) => {
                      const next = toApiDate(val);
                      handleEditDateChange("dob", next);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.dob,
                        helperText: errors.dob,
                      },
                    }}
                  />
                </Grid>

                {/* Gender */}
                <Grid item xs={12} sm={6}>
                  <TextField 
                    select 
                    fullWidth 
                    name="gender"
                    label="Gender" 
                    value={editingStudent.gender || ''}
                    onChange={handleEditInputChange}
                    error={!!errors.gender}
                    helperText={errors.gender}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>

                {/* Parent Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="parentName"
                    label="Parent Name"
                    value={editingStudent.parentName || ''}
                    onChange={handleEditInputChange}
                    onKeyPress={(e) => {
                      if (!/[A-Za-z\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    error={!!errors.parentName}
                    helperText={errors.parentName}
                  />
                </Grid>

                {/* Parent Phone Number */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.parentPhone}>
                    <InputLabel shrink htmlFor="parentPhone_edit">Parent Phone</InputLabel>
                    <PhoneInput
                      country="in"
                      placeholder=""
                      specialLabel=""
                      inputProps={{ id: 'parentPhone_edit', name: 'parentPhone', 'aria-label': 'Parent Phone' }}
                      countryCodeEditable={false}
                      enableSearch
                      value={phoneInputValue(editingStudent.parentPhone)}
                      onChange={(value) => {
                        setEditingStudent(prev => ({
                          ...prev,
                          parentPhone: value
                        }));
                        validateField('parentPhone', value);
                      }}
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        borderRadius: '4px',
                        border: errors.parentPhone ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)'
                      }}
                      containerStyle={{
                        marginTop: '8px',
                        marginBottom: errors.parentPhone ? '8px' : '23px'
                      }}
                    />
                    {errors.parentPhone && (
                      <FormHelperText>{errors.parentPhone}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="parentEmail"
                    type="email"
                    label="Parent Email"
                    value={editingStudent.parentEmail || ''}
                    onChange={handleEditInputChange}
                    onBlur={(e) => validateField('parentEmail', e.target.value)}
                    error={!!errors.parentEmail}
                    helperText={errors.parentEmail}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.parentWhatsapp}>
                    <InputLabel sx={{ top: '-5px' }} shrink htmlFor="parentWhatsapp_edit">
                      Parent WhatsApp
                    </InputLabel>
                    <PhoneInput
                      country="in"
                      specialLabel=""
                      inputProps={{ id: 'parentWhatsapp_edit', name: 'parentWhatsapp', 'aria-label': 'Parent WhatsApp' }}
                      countryCodeEditable={false}
                      enableSearch
                      value={phoneInputValue(editingStudent.parentWhatsapp)}
                      onChange={(value) => {
                        const formatted = value ? (value.startsWith('+') ? value : `+${value}`) : '';
                        setEditingStudent((prev) => ({ ...prev, parentWhatsapp: formatted }));
                        validateField('parentWhatsapp', formatted);
                      }}
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        paddingLeft: '48px',
                        borderRadius: '4px',
                        border: errors.parentWhatsapp ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                      }}
                      containerStyle={{
                        marginTop: '4px',
                        marginBottom: errors.parentWhatsapp ? '8px' : '23px',
                      }}
                    />
                    {errors.parentWhatsapp && <FormHelperText>{errors.parentWhatsapp}</FormHelperText>}
                  </FormControl>
                </Grid>

                {/* Class */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    name="classId"
                    label="Class"
                    value={editingStudent.classId || ''}
                    onChange={(e) => handleEditSelectChange('classId', e.target.value)}
                    error={!!errors.classId}
                    helperText={errors.classId || (isLoading.classes ? 'Loading classes...' : '')}
                    disabled={isLoading.classes}
                  >
                    <MenuItem value="">Select Class</MenuItem>
                    {Array.isArray(classes) && classes.map((cls) => (
                      <MenuItem key={cls.id || cls._id} value={cls.id || cls._id}>
                        {getClassDisplayLabel(cls)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Practical Batch */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    name="practicalBatchCode"
                    label="Practical Batch"
                    value={editingStudent.practicalBatchCode || ''}
                    onChange={(e) => handleEditSelectChange('practicalBatchCode', e.target.value)}
                    helperText={
                      editingClassPracticalBatches.length > 0
                        ? 'Optional. Choose one of the configured class batches.'
                        : 'No practical batches configured for this class'
                    }
                    disabled={!editingStudent.classId || editingClassPracticalBatches.length === 0}
                  >
                    <MenuItem value="">None</MenuItem>
                    {editingClassPracticalBatches.map((batchCode) => (
                      <MenuItem key={batchCode} value={batchCode}>
                        {batchCode}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Admission Date */}
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Admission Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={editingStudent.admissionDate ? new Date(editingStudent.admissionDate) : null}
                    onChange={(val) => {
                      const next = toApiDate(val);
                      handleEditDateChange("admissionDate", next);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.admissionDate,
                        helperText: errors.admissionDate,
                      },
                    }}
                  />
                </Grid>

                {/* Batch Start Date */}
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Batch Start Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={editingStudent.startBatchDate ? new Date(editingStudent.startBatchDate) : null}
                    onChange={(val) => {
                      const next = toApiDate(val);
                      handleEditDateChange("startBatchDate", next);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.startBatchDate,
                        helperText: errors.startBatchDate,
                      },
                    }}
                  />
                </Grid>

                {/* Batch End Date */}
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Batch End Date *"
                    format={DATE_DISPLAY_FORMAT}
                    value={editingStudent.endBatchDate ? new Date(editingStudent.endBatchDate) : null}
                    onChange={(val) => {
                      const next = toApiDate(val);
                      handleEditDateChange("endBatchDate", next);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.endBatchDate,
                        helperText: errors.endBatchDate,
                      },
                    }}
                  />
                </Grid>

                {/* Current Semester */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="currentSemester"
                    label="Current Semester"
                    value={editingStudent.currentSemester || ''}
                    onChange={handleEditInputChange}
                    error={!!errors.currentSemester}
                    helperText={errors.currentSemester}
                    placeholder="e.g. 3rd"
                  />
                </Grid>

                {/* Current Year */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    name="currentYear"
                    label="Current Year"
                    value={editingStudent.currentYear || ''}
                    onChange={handleEditInputChange}
                    inputProps={{ min: 1, max: 8, maxLength: 1, inputMode: 'numeric' }}
                    error={!!errors.currentYear}
                    helperText={errors.currentYear || "Enter 1 digit (1-8)"}
                    placeholder="e.g. 2"
                  />
                </Grid>

                {/* Address Search */}
                <Grid item xs={12}>
                  <AddressAutocomplete
                    placeholder="Search address (e.g. Chandigarh, Delhi)"
                    countryRestriction="in"
                    onAddressSelect={(address) => {
                      const updates = {
                        street: address.street || '',
                        city: address.city || '',
                        state: address.state || '',
                        country: address.country || 'India',
                        pinCode: address.pinCode || '',
                      };
                      setEditingStudent(prev => ({
                        ...prev,
                        ...updates
                      }));
                    }}
                  />
                </Grid>

                {/* Street */}
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    name="street" 
                    label="Street" 
                    value={editingStudent.street || ''}
                    onChange={handleEditInputChange}
                    error={!!errors.street}
                    helperText={errors.street}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    name="city" 
                    label="City" 
                    value={editingStudent.city || ''}
                    onChange={handleEditCityStateCountryChange}
                    onKeyDown={blockNumberKeyDown}
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                </Grid>

                {/* State */}
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    name="state" 
                    label="State" 
                    value={editingStudent.state || ''}
                    onChange={handleEditCityStateCountryChange}
                    onKeyDown={blockNumberKeyDown}
                    error={!!errors.state}
                    helperText={errors.state}
                  />
                </Grid>

                {/* Country */}
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    name="country" 
                    label="Country" 
                    value={editingStudent.country || 'India'}
                    onChange={handleEditCityStateCountryChange}
                    onKeyDown={blockNumberKeyDown}
                    error={!!errors.country}
                    helperText={errors.country}
                  />
                </Grid>

                {/* Pincode */}
                <Grid item xs={12} sm={6}>
                 <TextField
  fullWidth
  name="pinCode"
  label="Pincode"
  value={editingStudent.pinCode || ""}
  onChange={(e) => {
    // allow only numbers
    const value = e.target.value.replace(/[^0-9]/g, "");

    setEditingStudent({
      ...editingStudent,
      pinCode: value,
    });

    // clear error while typing
    if (errors.pinCode) {
      setErrors(prev => {
        const { pinCode, ...rest } = prev || {};
        return rest;
      });
    }
  }}
  inputProps={{ maxLength: 9 }}
  error={
    !!errors.pinCode ||
    (editingStudent.pinCode &&
      (editingStudent.pinCode.length < 3 ||
        editingStudent.pinCode.length > 9))
  }
  helperText={
    errors.pinCode ||
    (editingStudent.pinCode &&
      (editingStudent.pinCode.length < 3 ||
        editingStudent.pinCode.length > 9)
      ? "Pincode must be between 3 and 9 digits"
      : "")
  }
/>

                

                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setEditStudentDialogOpen(false);
              setEditingStudent(null);
              setErrors({});
            }}
            color="inherit"
            disabled={updateStudentMutation.isPending || parentPrefillLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditStudent}
            variant="contained"
            color="primary"
            disabled={updateStudentMutation.isPending || parentPrefillLoading || !editingStudent}
            startIcon={updateStudentMutation.isPending ? <CircularProgress size={16} /> : <Edit />}
          >
            {updateStudentMutation.isPending ? 'Updating...' : 'Update Student'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Student"
        message={`Are you sure you want to delete ${studentToDelete?.name || 'this student'}? This action cannot be undone.`}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setStudentToDelete(null);
        }}
        onConfirm={handleDeleteStudent}
        loading={deleteStudentMutation.isPending}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="error"
      />

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search students..."
              size="small"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPaginationModel((p)=>({ ...p, page: 0 })); }}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            
            <DepartmentModuleFilterSelect
              labelId="students-department-filter"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              instituteId={effectiveInstituteId}
              showAllOption={showAllDepartmentsOption}
              sx={{ minWidth: 200 }}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Class</InputLabel>
              <Select label="Class" value={filterClassId} onChange={(e) => { setFilterClassId(e.target.value); setFilterBatchCode("all"); }}>
                <MenuItem value="all">All classes</MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id || c._id} value={c.id || c._id}>{getClassDisplayLabel(c)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={!filterClassId || filterClassId === "all" || classBatchOptions.length === 0}>
              <InputLabel>Batch</InputLabel>
              <Select label="Batch" value={filterBatchCode} onChange={(e) => setFilterBatchCode(e.target.value)}>
                <MenuItem value="all">All batches</MenuItem>
                {classBatchOptions.map((b) => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={activeTab}
                onChange={(e) => setActiveTab(Number(e.target.value))}
              >
                <MenuItem value={0}>All Students</MenuItem>
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={2}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {studentsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : studentsError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load students. Please try again.
            </Alert>
          ) : (
            <DataGrid
              rows={filteredStudents}
              columns={columns}
              autoHeight
              loading={studentsLoading || studentsFetching || updateStatusMutation.isPending}
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => {
                console.log('Pagination model changing from', paginationModel, 'to', newModel);
                setPaginationModel(newModel);
              }}
              rowCount={
                (() => {
                  const total = 
                    studentsResponse?.data?.total ||
                    studentsResponse?.data?.totalCount ||
                    studentsResponse?.data?.totalRecords ||
                    studentsResponse?.total ||
                    studentsResponse?.totalCount ||
                    studentsResponse?.totalRecords ||
                    studentsResponse?.data?.pagination?.total ||
                    studentsResponse?.pagination?.total;
                  const totalNum = Number(total);
                  return totalNum > 0 ? totalNum : students.length || 0;
                })()
              }
              paginationMode="server"
              sx={{ border: "none" }}
              getRowId={(row) => row.id}
              disableColumnFilter
              disableColumnMenu
            />
          )}
        </CardContent>
      </Card>

      {/* View Dialog - clear content only after close animation to avoid dividers showing "from start to end" */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          if (e) e.stopPropagation();
          setViewDialogOpen(false);
        }}
        TransitionProps={{
          onExited: () => {
            setFullStudentData(null);
            setAnchorEl(null);
          },
        }}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Student Details
            </Typography>
            {canRevealStudentPii && hasRevealableMaskedPii(fullStudentData) && (
              <Tooltip
                title={
                  isGfmOnlyReveal
                    ? "Reveal parent contact details (Guardian Faculty access)"
                    : "Reveal personal information"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="reveal personal information"
                    disabled={revealingStudentPii}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRevealStudentPii(
                        fullStudentData?.id || fullStudentData?.studentId || selectedStudent?.id
                      );
                    }}
                    sx={{ position: "absolute", right: 48, top: "50%", transform: "translateY(-50%)" }}
                  >
                    {revealingStudentPii ? <CircularProgress size={18} /> : <Visibility fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setViewDialogOpen(false);
              }}
              sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {fullStudentData && (
            <Box sx={{ mt: 1 }}>
              {/* Header Section */}
              <Box sx={{ textAlign: "center", mb: 3, pb: 2, borderBottom: 1, borderColor: "divider" }}>
                <Avatar
                  src={fullStudentData.profileImage || fullStudentData.user?.profileImage || undefined}
                  sx={{ width: 100, height: 100, mx: "auto", mb: 2, fontSize: 40 }}
                >
                  {!(fullStudentData.profileImage || fullStudentData.user?.profileImage)
                    ? (fullStudentData.fullName || fullStudentData.firstName || selectedStudent?.name || "S")[0].toUpperCase()
                    : null}
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {fullStudentData.fullName || `${fullStudentData.firstName || ''} ${fullStudentData.lastName || ''}`.trim() || selectedStudent?.name || 'N/A'}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
                  <Chip 
                    label={fullStudentData.studentId || fullStudentData.studentLoginID || 'N/A'} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={fullStudentData.status ? "Active" : "Inactive"} 
                    size="small" 
                    color={fullStudentData.status ? "success" : "default"}
                  />
                </Box>
              </Box>

              {/* Personal Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "primary.main" }}>
                  Personal Information
                </Typography>
                {isGfmOnlyReveal && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.5, fontStyle: "italic" }}
                  >
                    As Guardian Faculty, you can reveal only the parent contact
                    details. The student's own email and phone remain masked.
                  </Typography>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      First Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.firstName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.lastName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.email || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      PRN Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.prnNumber || fullStudentData.prn_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Roll Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.rollNumber || fullStudentData.roll_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Phone Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.phoneNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Gender
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.gender ? fullStudentData.gender.charAt(0).toUpperCase() + fullStudentData.gender.slice(1) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.dob 
                        ? displayDate(fullStudentData.dob, "N/A")
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Academic Information Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "primary.main" }}>
                  Academic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Class
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.className || fullStudentData.class || 'N/A'}
                      {fullStudentData.classSection && ` - Section ${fullStudentData.classSection}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Department
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.departmentName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Admission Date
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.admissionDate 
                        ? displayDate(fullStudentData.admissionDate, "N/A")
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Current Semester
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.currentSemester || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Current Year
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.currentYear || 'N/A'}
                    </Typography>
                  </Grid>
                  {fullStudentData.startBatchDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Batch Start Date
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {displayDate(fullStudentData.startBatchDate)}
                      </Typography>
                    </Grid>
                  )}
                  {fullStudentData.endBatchDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Batch End Date
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {displayDate(fullStudentData.endBatchDate)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Parent/Guardian Information Section */}
              {(fullStudentData.parentName || fullStudentData.parentPhone || fullStudentData.parentEmail || fullStudentData.parentWhatsapp) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "primary.main" }}>
                    Parent/Guardian Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Parent Name
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.parentName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Parent Phone
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.parentPhone || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Parent Email
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.parentEmail || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Parent WhatsApp
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.parentWhatsapp || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Address Information Section */}
              {(fullStudentData.street || fullStudentData.city || fullStudentData.state || fullStudentData.country || fullStudentData.pinCode) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "primary.main" }}>
                    Address Information
                  </Typography>
                  <Grid container spacing={2}>
                    {fullStudentData.street && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Street
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {fullStudentData.street}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        City
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.city || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        State
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.state || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Country
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {fullStudentData.country || 'N/A'}
                      </Typography>
                    </Grid>
                    {fullStudentData.pinCode && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Pin Code
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {fullStudentData.pinCode}
                        </Typography>
                      </Grid>
                    )}
                    {fullStudentData.address && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Full Address
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {fullStudentData.address}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* System Information Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "primary.main" }}>
                  System Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Student ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.id || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      User ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {fullStudentData.userId || 'N/A'}
                    </Typography>
                  </Grid>
                  {fullStudentData.createdAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created At
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(fullStudentData.createdAt).toLocaleString('en-GB', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Grid>
                  )}
                  {fullStudentData.updatedAt && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last Updated
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {new Date(fullStudentData.updatedAt).toLocaleString('en-GB', { 
                          day: '2-digit', 
                          month: 'short', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              setViewDialogOpen(false);
            }}
          >
            Close
          </Button>
          <Button 
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              if (selectedStudent) {
                loadStudentForEdit(selectedStudent);
                setViewDialogOpen(false);
              }
            }}
            startIcon={<Edit />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Only render Menu when view dialog is closed to prevent it flashing in the middle on view close */}
      {!viewDialogOpen && (
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedStudent(null);
        }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedStudent) {
              loadStudentForEdit(selectedStudent);
              setAnchorEl(null);
            }
          }}
        >
          Edit Student
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedStudent) {
              setStudentToDelete(selectedStudent);
              setDeleteConfirmOpen(true);
              setAnchorEl(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete Student
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedStudent) {
              // Find full student data from API response
              const raw = studentsResponse?.data?.students ||
                          studentsResponse?.data?.rows ||
                          studentsResponse?.data?.items ||
                          studentsResponse?.data ||
                          studentsResponse?.students ||
                          studentsResponse?.rows ||
                          studentsResponse?.items ||
                          [];
              const fullData = Array.isArray(raw) 
                ? raw.find(s => {
                    const sid = s.id || s.uuid || s.studentId;
                    return Number(sid) === Number(selectedStudent.id);
                  })
                : null;
              setFullStudentData(fullData || selectedStudent);
              setViewDialogOpen(true);
              setAnchorEl(null);
            }
          }}
        >
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Profile
        </MenuItem>
        <MenuItem>View Attendance</MenuItem>
        <MenuItem>View Assignments</MenuItem>
        <MenuItem>Send Message</MenuItem>
      </Menu>
      )}

      {/* Import Wizard */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityType="students"
        instituteId={currentUser?.instituteId}
        onImportComplete={() => {
          // Invalidate students query to refresh the list
          queryClient.invalidateQueries({ queryKey: ["institute-students"] });
        }}
      />



      
      <Dialog open={promoteDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; closePromoteDialog(); }} maxWidth="md" fullWidth>
        <DialogTitle>Academic Transition</DialogTitle>
        <DialogContent dividers>
          {promoteContextLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Loading student academic context…
              </Typography>
            </Box>
          )}
          <Stepper activeStep={promoteStep} sx={{ mb: 2 }}>
            <Step><StepLabel>Current</StepLabel></Step>
            <Step><StepLabel>Transition</StepLabel></Step>
            <Step><StepLabel>Subjects & Resets</StepLabel></Step>
            <Step><StepLabel>Preview</StepLabel></Step>
          </Stepper>

          {promoteStep === 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Student</Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>{promoteStudent?.name || "-"}</Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Current Academic Info</Typography>
                      <Typography variant="body2"><b>Class</b>: {promoteContextLoading ? "…" : (promoteContext?.currentClass?.name || promoteContext?.activeEnrollment?.classId || "-")}</Typography>
                      <Typography variant="body2"><b>Section</b>: {promoteContextLoading ? "…" : (promoteContext?.currentClass?.section || promoteContext?.activeEnrollment?.section || "-")}</Typography>
                      <Typography variant="body2"><b>Status</b>: {promoteContextLoading ? "…" : (promoteContext?.activeEnrollment?.enrollmentStatus || "-")}</Typography>
                      <Typography variant="body2"><b>Session</b>: {promoteContextLoading ? "…" : (promoteContext?.activeEnrollment?.academicYearLabel || promoteContext?.currentClass?.academicYear || "-")}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Current Subjects</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {promoteContextLoading
                          ? "Loading…"
                          : (promoteContext?.currentSubjects || []).map((s) => s?.name).filter(Boolean).join(", ") || "—"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {promoteStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="promote-session-label">To Session (Academic Year)</InputLabel>
                  <Select
                    labelId="promote-session-label"
                    label="To Session (Academic Year)"
                    value={promoteAcademicYearId}
                    onChange={(e) => setPromoteAcademicYearId(e.target.value)}
                  >
                    {academicYears.map((y) => (
                      <MenuItem key={y.id} value={String(y.id)}>
                        {y.academicYear} {y.status ? `(${y.status})` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Effective Date"
                  value={promoteEffectiveDate}
                  onChange={(v) => setPromoteEffectiveDate(v)}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="promote-to-class-label">To Class</InputLabel>
                  <Select
                    labelId="promote-to-class-label"
                    label="To Class"
                    value={promoteToClassId}
                    onChange={(e) => setPromoteToClassId(e.target.value)}
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.id || cls._id} value={String(cls.id || cls._id)}>
                        {getClassDisplayLabel(cls)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="promote-type-label">Transition Type</InputLabel>
                  <Select
                    labelId="promote-type-label"
                    label="Transition Type"
                    value={promoteType}
                    onChange={(e) => setPromoteType(e.target.value)}
                  >
                    <MenuItem value="PROMOTION">Promotion</MenuItem>
                    <MenuItem value="CLASS_CHANGE">Class Change</MenuItem>
                    <MenuItem value="SECTION_CHANGE">Section Change</MenuItem>
                    <MenuItem value="SESSION_CHANGE">Session Change</MenuItem>
                    <MenuItem value="STATUS_CHANGE">Status Change</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="promote-status-label">New Status</InputLabel>
                  <Select
                    labelId="promote-status-label"
                    label="New Status"
                    value={promoteEnrollmentStatus}
                    onChange={(e) => setPromoteEnrollmentStatus(e.target.value)}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="DETAINED">Detained</MenuItem>
                    <MenuItem value="REPEATER">Repeater</MenuItem>
                    <MenuItem value="GRADUATED">Graduated</MenuItem>
                    <MenuItem value="TRANSFERRED">Transfer Certificate</MenuItem>
                    <MenuItem value="DROPPED">Dropout</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Practical Batch Code"
                  value={promotePracticalBatchCode}
                  onChange={(e) => setPromotePracticalBatchCode(e.target.value)}
                  placeholder="e.g. A / B"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="Remarks"
                  value={promoteReason}
                  onChange={(e) => setPromoteReason(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {promoteStep === 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Subjects (To Class)</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="promote-subjects-label">Selected Subjects</InputLabel>
                <Select
                  labelId="promote-subjects-label"
                  label="Selected Subjects"
                  multiple
                  value={selectedSubjectIds}
                  onChange={(e) => setSelectedSubjectIds(e.target.value)}
                  renderValue={(selected) => {
                    const ids = Array.isArray(selected) ? selected : [];
                    const names = toClassSubjects.filter((s) => ids.includes(Number(s.id))).map((s) => s.name).filter(Boolean);
                    return names.join(", ") || "—";
                  }}
                >
                  {toClassSubjects.map((s) => (
                    <MenuItem key={s.id} value={Number(s.id)}>
                      <Checkbox checked={selectedSubjectIds.includes(Number(s.id))} />
                      <Typography variant="body2">{s.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Auto-selected compulsory subjects where available.</FormHelperText>
              </FormControl>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>ATKT / Backlog (carry forward)</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel id="promote-backlog-label">Backlog Subjects</InputLabel>
                <Select
                  labelId="promote-backlog-label"
                  label="Backlog Subjects"
                  multiple
                  value={selectedBacklogSubjectIds}
                  onChange={(e) => setSelectedBacklogSubjectIds(e.target.value)}
                  renderValue={(selected) => {
                    const ids = Array.isArray(selected) ? selected : [];
                    const names = toClassSubjects.filter((s) => ids.includes(Number(s.id))).map((s) => s.name).filter(Boolean);
                    return names.join(", ") || "—";
                  }}
                >
                  {toClassSubjects.map((s) => (
                    <MenuItem key={s.id} value={Number(s.id)}>
                      <Checkbox checked={selectedBacklogSubjectIds.includes(Number(s.id))} />
                      <Typography variant="body2">{s.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Reset / Archive Options</Typography>
              <Grid container spacing={1}>
                {Object.entries(resetOptions).map(([key, val]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(val)}
                          onChange={(e) => setResetOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
                        />
                      }
                      label={key}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {promoteStep === 3 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will create a new enrollment and archive the previous one. Historical data remains accessible via enrollment history.
              </Alert>
              <Typography variant="subtitle2">Preview</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Student</b>: {promoteStudent?.name || "-"}
              </Typography>
              <Typography variant="body2">
                <b>From</b>: {promoteContext?.currentClass?.name || promoteContext?.activeEnrollment?.classId || "-"} / {promoteContext?.currentClass?.section || promoteContext?.activeEnrollment?.section || "-"} ({promoteContext?.activeEnrollment?.academicYearLabel || promoteContext?.currentClass?.academicYear || "-"})
              </Typography>
              <Typography variant="body2">
                <b>To</b>: {classes.find((c) => String(c.id || c._id) === String(promoteToClassId)) ? getClassDisplayLabel(classes.find((c) => String(c.id || c._id) === String(promoteToClassId))) : promoteToClassId || "-"}
                {promoteAcademicYearId ? ` / ${academicYears.find((y) => String(y.id) === String(promoteAcademicYearId))?.academicYear || ""}` : ""}
              </Typography>
              <Typography variant="body2"><b>Type</b>: {promoteType}</Typography>
              <Typography variant="body2"><b>Status</b>: {promoteEnrollmentStatus}</Typography>
              <Typography variant="body2"><b>Effective</b>: {promoteEffectiveDate ? displayDate(promoteEffectiveDate, DATE_DISPLAY_FORMAT) : "-"}</Typography>
              <Typography variant="body2"><b>Subjects</b>: {toClassSubjects.filter((s) => selectedSubjectIds.includes(Number(s.id))).map((s) => s.name).filter(Boolean).join(", ") || "—"}</Typography>
              <Typography variant="body2"><b>Backlogs</b>: {toClassSubjects.filter((s) => selectedBacklogSubjectIds.includes(Number(s.id))).map((s) => s.name).filter(Boolean).join(", ") || "—"}</Typography>
              <Typography variant="body2"><b>Remarks</b>: {promoteReason || "—"}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePromoteDialog} disabled={promoting}>
            Cancel
          </Button>
          <Button
            onClick={() => setPromoteStep((s) => Math.max(0, s - 1))}
            disabled={promoting || promoteContextLoading || promoteStep === 0}
          >
            Back
          </Button>
          {promoteStep < 3 ? (
            <Button
              variant="contained"
              onClick={() => {
                if (promoteStep === 1 && !promoteToClassId) {
                  showToast("Please select the new class", "error");
                  return;
                }
                setPromoteStep((s) => Math.min(3, s + 1));
              }}
              disabled={promoting || promoteContextLoading || (promoteStep === 1 && !promoteToClassId)}
            >
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={submitPromotion} disabled={promoting || promoteContextLoading}>
              {promoting ? "Processing..." : "Confirm & Execute"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={promotionLogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setPromotionLogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Student promotion history</DialogTitle>
        <DialogContent dividers>
          <PromotionHistoryLogPanel classOptions={promotionClassFilterOptions} refreshKey={promotionLogRefresh} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromotionLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default Students;
